import { Injectable, Inject, PLATFORM_ID, Optional } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import {
  Observable,
  catchError,
  finalize,
  map,
  filter,
  take,
  throwError,
  from,
  forkJoin,
  switchMap,
  of,
  mergeMap,
  toArray,
  concatMap,
  scan,
  takeLast,
  timeout,
  tap,
  EMPTY,
} from 'rxjs';
import { isPlatformBrowser } from '@angular/common';

import { environment } from 'src/app/environments/environments';
import {
  Actions,
  Intents,
  Menu,
  Message,
  Bots,
  Catalogs,
  Templates,
  Labels,
  Orders,
  Widgets,
  Products,
  Options,
  Variants,
  Facets,
  Categories,
  users,
  DraftOrders,
  DefaultWorkspace,
} from 'src/app/features/models/list.model';
import { ToolsService } from './tools.service';
import { WhereFilterOp } from '@firebase/firestore-types';
import firebase from 'firebase/compat/app';
import 'firebase/compat/functions';
import 'firebase/compat/firestore';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { LoggingService } from './Logging.service';
import { RemoteApiService } from './remote-api.service';
import { AuthService } from 'src/app/features/services/auth/auth.service';
import {
  FIXED_MESSAGE_DEFAULTS,
  getFixedMessageDefaultSource,
} from 'src/app/shared/constants/fixed-messages.defaults';
import { NotificationService } from 'src/app/shared/services/notification.service';
import {
  BOT_INVOCATION_CLASSIFIER_INSTRUCTION,
  CALLBOT_FATAL_ERROR_DESCRIPTION,
  CALLBOT_FATAL_ERROR_INSTRUCTION,
  CALLBOT_HANDOVER_SCENARIO_DEFAULT_INSTRUCTIONS,
  END_CONVERSATION_SCENARIO_DEFAULT_INSTRUCTIONS,
  ERROR_SCENARIO_DEFAULT_INSTRUCTIONS,
  FALLBACK_SCENARIO_DEFAULT_INSTRUCTIONS,
  HANDOVER_INSTRUCTION_REPLACEMENTS,
  resolveScenarioDescription,
  resolveScenarioDisplayName,
  SCENARIO_RENAME_MAP,
} from 'src/app/shared/constants/behavior-scenario.defaults';

interface Condition {
  left: any;
  operator: any;
  right: any;
}

@Injectable()
export class DataAccessService {
  urlUpload: any;
  getThemeID(themeID: any) {
    throw new Error('Method not implemented.');
  }

  docid!: string;
  idParent!: string;
  idaction!: string;

  private hasAuthenticatedUser(): boolean {
    const user = this.auth.getCurrentUser();
    // Also verify Firebase auth user exists to prevent queries after full sign-out.
    const fbUser = this.afs?.firestore?.app?.auth?.()?.currentUser;
    return !!(
      user &&
      user.id &&
      this.auth.isLoggedIn() &&
      fbUser &&
      fbUser.uid
    );
  }

  constructor(
    private auth: AuthService,
    private loggingService: LoggingService,
    private afs: AngularFirestore,
    private tools: ToolsService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private storage: AngularFireStorage,
    private RAS: RemoteApiService,
    private notificationService: NotificationService,
  ) {}

  private getRequiredUserId(): string {
    const currentUser = this.auth.getCurrentUser();
    if (!currentUser?.id) {
      throw new Error(
        'User ID is required but no authenticated user was found.',
      );
    }
    return currentUser.id;
  }

  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  private get firestore(): AngularFirestore | null {
    return this.isBrowser ? this.afs : null;
  }

  testData$() {
    if (!this.firestore) {
      return of([]);
    }
    return this.firestore.collection<any>('test-coll').valueChanges();
  }
  getWidgetsNonDefault(collection, idBOT): Observable<Widgets[]> {
    return this.getCollectionWhere(
      [`${collection}/${idBOT}/widgets/`],
      [
        {
          left: 'show',
          operator: '==',
          right: true,
        },
      ],
    );
  }
  getDocument(docPath: string) {
    if (!docPath || typeof docPath !== 'string' || docPath.trim() === '') {
      this.loggingService.error(
        'Invalid document path in getDocument:',
        docPath,
      );
      return new Observable((observer) => {
        observer.error(new Error('Invalid document path provided'));
        observer.complete();
      });
    }

    // Avoid querying when user is logged out; prevents permission errors after logout.
    if (!this.hasAuthenticatedUser()) {
      return of({
        id: null,
        path: docPath,
        error: true,
        errorCode: 'unauthenticated',
      });
    }

    return this.afs
      .doc(docPath)
      .snapshotChanges()
      .pipe(
        map((a) => {
          const data = a.payload.data() as any;
          const id = a.payload.id;
          const path = a.payload.ref.path;
          return {
            id,
            path,
            ...data,
          }; // => flattened data : {a:1
        }),
        catchError((error) => {
          this.loggingService.error('[Firebase ERROR in getDocument()]', error);
          this.loggingService.error('Path attempted:', docPath);

          // Return error object with more details
          return of({
            id: null,
            path: docPath,
            error: true,
            errorCode: error?.code || 'unknown',
            errorMessage: error?.message || 'Unknown error',
          });
        }),
      );
  }
  getDocumentWithoutRealTime(docPath: string) {
    if (!this.firestore) {
      return of(null);
    }
    return this.firestore
      .doc(docPath)
      .get()
      .pipe(
        map((a) => {
          const data = a.data() as any;
          const id = a.id;
          const path = a.ref.path;
          return {
            id,
            path,
            ...data,
          };
        }),
        take(1), // Cette ligne assure que la fonction s'exÃ©cute une seule fois
      );
  }
  updateDocument(docPath: string, data: any): Promise<void> {
    return this.afs.doc(docPath).update(data);
  }
  getCollectionWhere(
    pathArray: string[],
    conditions: Condition[],
  ): Observable<any[]> {
    if (!this.firestore) {
      return of([]);
    }
    // Avoid querying when user is logged out; prevents permission errors after logout.
    if (!this.hasAuthenticatedUser()) {
      return of([]);
    }
    const coPath = pathArray.join('/');
    const queryConditions = [...(conditions || [])];
    return this.firestore
      .collection<any>(coPath, (ref) => {
        // Copy before pop: AngularFire may invoke this factory more than once.
        const conds = [...queryConditions];
        const cnd0 = conds.pop();
        if (!this.tools.isUndefined(cnd0)) {
          let query = ref
            .where(cnd0?.left, cnd0?.operator, cnd0?.right)
            .orderBy('timeStamp', 'desc');
          conds.forEach(({ left, operator, right }: Condition) => {
            query = query.where(left, operator, right);
          });
          return query;
        } else {
          return ref;
        }
      })
      .snapshotChanges()
      .pipe(
        map((actions) => {
          return actions.map((a) => {
            const data = a.payload.doc.data() as any;
            const id = a.payload.doc.id;
            const path = a.payload.doc.ref.path;
            return {
              id,
              path,
              ...data,
            };
          });
        }),
        catchError((error) => {
          // Suppress noisy logs when user is no longer authenticated.
          if (this.hasAuthenticatedUser()) {
            this.loggingService.error(
              '[Firebase ERROR in getDocument()]',
              error,
            );
            this.loggingService.error('Path attempted:', coPath);
          }
          return of([]);
        }),
      );
  }

  getCollectionWhereWithoutTime(
    pathArray: string[],
    conditions: Condition[],
  ): Observable<any[]> {
    // Avoid querying when user is logged out; prevents permission errors after logout.
    if (!this.hasAuthenticatedUser()) {
      return of([]);
    }
    const coPath = pathArray.join('/');
    // Create a copy of conditions to avoid mutating the original array
    const conditionsCopy = [...conditions];
    return this.afs
      .collection<any>(coPath, (ref) => {
        const cnd0 = conditionsCopy.pop();
        if (!this.tools.isUndefined(cnd0)) {
          let query = ref.where(cnd0?.left, cnd0?.operator, cnd0?.right);
          conditionsCopy.map(({ left, operator, right }: Condition) => {
            query = query.where(left, operator, right);
          });
          return query;
        } else {
          return ref;
        }
      })
      .snapshotChanges()
      .pipe(
        map((actions) => {
          return actions.map((a) => {
            const data = a.payload.doc.data() as any;
            const id = a.payload.doc.id;
            const path = a.payload.doc.ref.path;
            return {
              id,
              path,
              ...data,
            };
          });
        }),
        catchError((error) => {
          if (this.hasAuthenticatedUser()) {
            this.loggingService.error(
              '[Firebase ERROR in getDocument()]',
              error,
            );
            this.loggingService.error('Path attempted:', coPath);
          }
          return of([]);
        }),
      );
  }

  getCollectionWhereWithInequality(
    pathArray: string[],
    conditions: Condition[],
    inequalityField: string,
  ): Observable<any[]> {
    if (!this.hasAuthenticatedUser()) {
      return of([]);
    }
    const coPath = pathArray.join('/');
    // Create a copy of conditions to avoid mutating the original array
    const conditionsCopy = [...conditions];
    return this.afs
      .collection<any>(coPath, (ref) => {
        const cnd0 = conditionsCopy.pop();
        if (!this.tools.isUndefined(cnd0)) {
          // Order by the inequality field first, then by timeStamp
          let query = ref
            .where(cnd0?.left, cnd0?.operator, cnd0?.right)
            .orderBy(inequalityField, 'asc')
            .orderBy('timeStamp', 'desc');
          conditionsCopy.map(({ left, operator, right }: Condition) => {
            query = query.where(left, operator, right);
          });
          return query;
        } else {
          return ref;
        }
      })
      .snapshotChanges()
      .pipe(
        map((actions) => {
          return actions.map((a) => {
            const data = a.payload.doc.data() as any;
            const id = a.payload.doc.id;
            const path = a.payload.doc.ref.path;
            return {
              id,
              path,
              ...data,
            };
          });
        }),
        catchError((error) => {
          this.loggingService.error('[Firebase ERROR in getDocument()]', error);
          this.loggingService.error('Path attempted:', coPath);
          return of([]);
        }),
      );
  }

  getBotsforAnalytics(
    pathArray: string[],
    conditions: Condition[],
  ): Observable<any[]> {
    const coPath = pathArray.join('/');
    return this.afs
      .collection<any>(coPath, (ref) => {
        const cnd0 = conditions.pop();
        if (!this.tools.isUndefined(cnd0)) {
          let query = ref.where(cnd0?.left, cnd0?.operator, cnd0?.right);
          conditions.map(({ left, operator, right }: Condition) => {
            query = query.where(left, operator, right);
          });
          return query;
        } else {
          return ref;
        }
      })
      .snapshotChanges()
      .pipe(
        map((actions) => {
          return actions.map((a) => {
            const data = a.payload.doc.data() as any;
            const id = a.payload.doc.id;
            const path = a.payload.doc.ref.path;
            return this.afs
              .collection<any>(`${path}/appData/`)
              .snapshotChanges()
              .pipe(
                map((actions) => {
                  return actions.map((a) => {
                    const data = a.payload.doc.data() as any;

                    const id = a.payload.doc.id;

                    const path = a.payload.doc.ref.path;
                    return {
                      id,
                      path,
                      ...data,
                    };
                  });
                }),
              );
          });
        }),
      );
  }

  getCollectionWhereWithalphabeticalOrder(
    pathArray: string[],
    conditions: Condition[],
  ): Observable<any[]> {
    if (!this.hasAuthenticatedUser()) {
      return of([]);
    }
    const coPath = pathArray.join('/');
    return this.afs
      .collection<any>(coPath, (ref) => {
        const cnd0 = conditions.pop();
        if (!this.tools.isUndefined(cnd0)) {
          let query = ref.where(cnd0?.left, cnd0?.operator, cnd0?.right);
          conditions.map(({ left, operator, right }: Condition) => {
            query = query.where(left, operator, right);
          });
          return query;
        } else {
          return ref;
        }
      })
      .snapshotChanges()
      .pipe(
        map((actions) => {
          return actions.map((a) => {
            const data = a.payload.doc.data() as any;
            const id = a.payload.doc.id;
            const path = a.payload.doc.ref.path;
            return {
              id,
              path,
              ...data,
            };
          });
        }),
        catchError((error) => {
          this.loggingService.error('[Firebase ERROR in getDocument()]', error);
          this.loggingService.error('Path attempted:', coPath);
          return of([]);
        }),
      );
  }

  //new
  deleteDocumentCollectionWithSubCollection(user: any, path: string) {
    const callable = firebase.functions().httpsCallable('mintAdminToken');
    const obs = callable({
      uid: user.id,
    });
    obs.then(async (res: any) => {
      firebase
        .auth()
        .signInWithCustomToken(res.token)
        .then((connect) => {
          var deleteFn = firebase.functions().httpsCallable('recursiveDelete');
          deleteFn({ path: path })
            .then(function (result) {
              //	this.snackBar.open('Bot deleted', '', {duration: 4000});
            })
            .catch(() => { console.log('Something went wrong'); });
        });
    });
  }
  async deleteActionWithSubcollections(path: string): Promise<void> {
    if (!path || typeof path !== 'string' || path.trim() === '') {
      const error = new Error(
        'Invalid path provided to deleteActionWithSubcollections',
      );
      this.loggingService.error('Invalid path:', path);
      return Promise.reject(error);
    }

    const botRef = this.afs.doc(path);
    try {
      // Check if user is authenticated
      const userId = this.getRequiredUserId();
      if (!userId) {
        const error = new Error(
          'User must be authenticated to delete documents',
        );
        this.loggingService.error(
          'Authentication error in deleteActionWithSubcollections:',
          error,
        );
        return Promise.reject(error);
      }

      const subcollections = ['elements']; // add all expected subcollections here

      for (const sub of subcollections) {
        const subRef = botRef.collection(sub);
        const snapshot = await subRef.get().toPromise();

        if (snapshot && !snapshot.empty) {
          const batch = this.afs.firestore.batch();
          let deleteCount = 0;

          snapshot.forEach((doc) => {
            const docData = doc.data();
            // Only delete documents that belong to the current user
            if (docData && docData.userID === userId) {
              batch.delete(doc.ref);
              deleteCount++;
            } else {
              this.loggingService.warn(
                `Skipping deletion of ${doc.ref.path} - userID mismatch or missing`,
              );
            }
          });

          if (deleteCount > 0) {
            try {
              await batch.commit();
            } catch (batchError: any) {
              this.loggingService.error(
                `Error committing batch delete for ${sub}:`,
                batchError,
              );
              if (
                batchError?.code === 'permission-denied' ||
                batchError?.message?.includes(
                  'Missing or insufficient permissions',
                )
              ) {
              }
              // Continue with other subcollections even if one fails
            }
          }
        }
      }

      // Delete parent action document
      try {
        await botRef.delete();
      } catch (deleteError: any) {
        this.loggingService.error(
          'Error deleting action document:',
          deleteError,
        );
        this.loggingService.error('Path attempted:', path);

        if (
          deleteError?.code === 'permission-denied' ||
          deleteError?.message?.includes('Missing or insufficient permissions')
        ) {
        } else if (deleteError?.code === 'not-found') {
        } else {
        }
        throw deleteError;
      }
    } catch (err: any) {
      this.loggingService.error(
        'âŒ Error deleting action or its subcollections:',
        err,
      );
      this.loggingService.error('Path attempted:', path);

      if (
        err?.code === 'permission-denied' ||
        err?.message?.includes('Missing or insufficient permissions')
      ) {
      } else if (err?.code === 'not-found') {
      } else {
      }

      return Promise.reject(err);
    }
  }

  // to remoove
  getbot(idBot: string): Observable<any[]> {
    return this.afs
      .collection<any>(`${this.auth.isSuperAdmin() ? 'default-bots' : 'bots'}`)
      .snapshotChanges()
      .pipe(
        map((actions) => {
          return actions
            .map((a) => {
              const data = a.payload.doc.data() as any;
              const id = a.payload.doc.id;
              const path = a.payload.doc.ref.path;
              return {
                id,
                path,
                ...data,
              };
            })
            .find((x) => x.id == idBot);
        }),
      );
  }
  // to remoove
  getTheme(idTheme: string): Observable<any> {
    if (!idTheme) {
      return of(null);
    }
    // Read a single theme document instead of scanning the whole collection.
    // This aligns with rules that allow auth'd users to read and avoids unrelated docs causing denials.
    const docPath = `themes/${idTheme}`;
    return this.afs
      .doc<any>(docPath)
      .snapshotChanges()
      .pipe(
        map((a) => {
          const data = a.payload.data() as any;
          const id = a.payload.id;
          const path = a.payload.ref.path;
          return { id, path, ...data };
        }),
        take(1),
        catchError((error) => {
          this.loggingService.error('[Firebase ERROR in getTheme()]', error);
          this.loggingService.error('Path attempted:', docPath);
          return of(null);
        }),
      );
  }

  /* ------------------------Actions------------------------ */

  addAction(
    collection: any,
    idBot: string,
    actID: string,
    actionName: string,
    builtIn: boolean,
    event: any,
    intention: any,
    show: boolean,
    builtTemplate: boolean,
    elements: any,
  ): Promise<any> {
    let userID: string;
    try {
      userID = this.getRequiredUserId();
    } catch (error) {
      return Promise.reject(error);
    }
    const actionRef = this.afs
      .collection(`${collection}/${idBot}/actions/`)
      .doc(actID);
    return actionRef
      .set({
        name: actionName,
        show: show,
        builtTemplate: builtTemplate,
        built: builtIn,
        event: event,
        intention: intention,
        userID,
        //rang: rang,
        timeStamp: Date.now(),
      })
      .then(() => {
        //TODO: add index into element, the order at which this element response will be shown @ bit's responses
        const elementPayload = { ...(elements || {}), userID };
        actionRef.collection('elements').add(elementPayload);
      })
      .catch(() => { console.log('Something went wrong'); })
      .then(() => {
        return actionRef.snapshotChanges().pipe(
          map((a) => {
            const data = a.payload.data() as Actions;
            const id = a.payload.id;
            const path = a.payload.ref.path;
            return {
              id,
              ...data,
            };
          }),
        );
      });
  }

  /*
	addAction(collection: any, idBot: string, actID: string, actionName: string, builtIn: boolean, event: any, intention: any, show: boolean, builtTemplate: boolean, elements: any): Promise<any> {
		let userID: string;
		try {
			userID = this.getRequiredUserId();
		} catch (error) {
			return Promise.reject(error);
		}
		const actionRef = this.afs.collection(`${collection}/${idBot}/actions/`).doc(actID)
		return actionRef.set({
			"name": actionName,
			"show": show,
			builtTemplate: builtTemplate,
			built: builtIn,
			event: event,
			intention: intention,
			userID,
			timeStamp: Date.now()
		})
		.then(() => {
		  
			if (elements && Object.keys(elements).length > 0) {
				const elementPayload = { ...elements, userID };
				return actionRef.collection('elements').add(elementPayload).then(() => {});
			}
		   
			return;
		})
		.catch(e => {
			console.log(e);
			throw e;
		})
		.then(() => {
			return actionRef.snapshotChanges()
				.pipe(
					map(a => {
						const data = a.payload.data() as Actions;
						const id = a.payload.id;
						const path = a.payload.ref.path;
						return {
							id,
							...data
						};
					}),
				);
		});
	}
	*/
  addFollowUp(
    collection: any,
    idBot: string,
    actID: string,
    actionName: string,
  ): Promise<any> {
    const actionRef = this.afs
      .collection(`${collection}/${idBot}/FollowUpMessages/`)
      .doc(actID);
    return actionRef
      .set({
        name: actionName,
        //rang: rang,
        timeStamp: Date.now(),
      })
      .then(() => {
        return actionRef.snapshotChanges().pipe(
          map((a) => {
            const data = a.payload.data() as Actions;
            const id = a.payload.id;
            const path = a.payload.ref.path;
            return {
              id,
              ...data,
            };
          }),
        );
      });
  }

  addMarketingMessage(
    collection: any,
    idBot: string,
    actID: string,
    actionName: string,
  ): Promise<any> {
    const actionRef = this.afs
      .collection(`${collection}/${idBot}/MarketingMessages/`)
      .doc(actID);
    return actionRef
      .set({
        name: actionName,
        //rang: rang,
        timeStamp: Date.now(),
      })
      .then(() => {
        return actionRef.snapshotChanges().pipe(
          map((a) => {
            const data = a.payload.data() as Actions;
            const id = a.payload.id;
            const path = a.payload.ref.path;
            return {
              id,
              ...data,
            };
          }),
        );
      });
  }

  updateResponseActionElement(docpath: string, element: any): Promise<any> {
    return this.afs
      .doc(docpath)
      .update(element)
      .then(() => {
        // this.snackBar.open('success', '', {
        // 	duration: 4000
        // });
      })
      .catch(() => { console.log('Something went wrong'); });
  }

  addResponseActionElement(
    idBOT: string,
    actID: string,
    element: any,
  ): Promise<any> {
    let userID: string;
    try {
      userID = this.getRequiredUserId();
    } catch (error) {
      return Promise.reject(error);
    }
    const elementWithUserId = { ...element, userID };
    return this.afs
      .collection(`bots/${idBOT}/actions/`)
      .doc(actID)
      .collection('elements')
      .add(elementWithUserId);
  }
  /*
		addResponseActionElementDesign(collection: any, idBOT: string, actID: string, element: any): Promise<any> {
			let userID: string;
			try {
				userID = this.getRequiredUserId();
			} catch (error) {
				return Promise.reject(error);
			}
			const elementWithUserId = { ...element, userID };
			return this.afs.collection(`${collection}/${idBOT}/actions/`).doc(actID).collection('elements').add(elementWithUserId)
				// this.afs.doc(docpath).update(element)
				.then(
					() => {
	
						console.log('success');
					}
				).catch(
					e => {
	
						console.log(e);
					});
	
		}
	*/
  addResponseActionElementDesign(
    collection: any,
    idBOT: string,
    actID: string,
    element: any,
  ): Promise<any> {
    let userID: string;
    try {
      userID = this.getRequiredUserId();
    } catch (error) {
      return Promise.reject(error);
    }
    const elementWithUserId = { ...element, userID };

    return this.afs
      .collection(`${collection}/${idBOT}/actions/`)
      .doc(actID)
      .collection('elements')
      .add(elementWithUserId)
      .then((docRef) => {
        return docRef;
      })
      .catch((e) => {
        console.log('Something went wrong');
        throw e;
      });
  }

  /**
   * Bot carousel template elements: `/{collection}/{botId}/Templates/{templateId}/elements/{elementId}`
   * Same pattern as `actions/{actionId}/elements` used by action-response-fb.
   */
  getTemplateElements(
    collection: string,
    idBOT: string,
    templateId: string,
    templatesSubcollection = 'Templates',
  ): Observable<any[]> {
    const parentPath = `${collection}/${idBOT}/${templatesSubcollection}/${templateId}`;
    return this.afs
      .collection<any>(`${parentPath}/elements`)
      .snapshotChanges()
      .pipe(
        map((actions) => {
          return actions
            .map((a) => {
              const data = a.payload.doc.data() as any;
              const id = a.payload.doc.id;
              const isEmpty = Object.keys(data).length === 0;
              const path = a.payload.doc.ref.path;
              return {
                id,
                path,
                isEmpty,
                ...data,
              };
            })
            .sort((a, b) => ((a.index ?? 0) > (b.index ?? 0) ? 1 : -1));
        }),
      );
  }

  /**
   * Parent bot carousel template doc: `/{collection}/{botId}/Templates/{templateId}`.
   * The JSON schema string field `template` (e.g. template_cards_array_1) is stored here once per template.
   */
  getBotCarouselTemplateDoc(
    collection: string,
    idBOT: string,
    templateId: string,
    templatesSubcollection = 'Templates',
  ): Observable<any | null> {
    const path = `${collection}/${idBOT}/${templatesSubcollection}/${templateId}`;
    return this.afs
      .doc<any>(path)
      .get()
      .pipe(
        map((snapshot) => {
          if (!snapshot?.exists) {
            return null;
          }
          const data = snapshot.data() as any;
          return {
            id: snapshot.id,
            path: snapshot.ref.path,
            ...data,
          };
        }),
      );
  }

  /**
   * Persists the carousel schema definition string on `Templates/{templateId}` (not on element docs).
   */
  updateCarouselTemplateSchema(
    collection: string,
    idBOT: string,
    templateId: string,
    template: string | null,
    templatesSubcollection = 'Templates',
  ): Promise<void> {
    const path = `${collection}/${idBOT}/${templatesSubcollection}/${templateId}`;
    const ref = this.afs.doc(path).ref;
    if (template === null || template === undefined) {
      return ref
        .update({ template: firebase.firestore.FieldValue.delete() })
        .catch(() => { console.log('Something went wrong'); return Promise.resolve(); });
    }
    return ref.set({ template }, { merge: true }).catch((e) => {
      console.log('Something went wrong');
      throw e;
    });
  }

  /** Lists parent docs under `/{collection}/{botId}/{templatesSubcollection}`. */
  listBotCarouselTemplates(
    collection: string,
    idBOT: string,
    templatesSubcollection = 'Templates',
  ): Observable<
    Array<{ id: string; path: string; name?: string; functionToolName?: string; isDefault?: boolean; type?: string }>
  > {
    return this.afs
      .collection(`${collection}/${idBOT}/${templatesSubcollection}`)
      .snapshotChanges()
      .pipe(
        map((actions) =>
          actions.map((a) => {
            const data = a.payload.doc.data() as Record<string, unknown>;
            return {
              id: a.payload.doc.id,
              path: a.payload.doc.ref.path,
              name: (data['name'] as string) || a.payload.doc.id,
              functionToolName: data['functionToolName'] as string | undefined,
              isDefault: data['isDefault'] as boolean | undefined,
              type: data['type'] as string | undefined,
            };
          }),
        ),
      );
  }

  /** Deletes template parent doc and its `elements` subcollection docs. */
  async deleteBotCarouselTemplate(
    collection: string,
    idBOT: string,
    templateId: string,
    templatesSubcollection = 'Templates',
  ): Promise<void> {
    const basePath = `${collection}/${idBOT}/${templatesSubcollection}/${templateId}`;
    const elementsSnap = await this.afs
      .collection(`${basePath}/elements`)
      .ref.get();
    const batch = this.afs.firestore.batch();
    elementsSnap.docs.forEach((doc) => batch.delete(doc.ref));
    batch.delete(this.afs.doc(basePath).ref);
    await batch.commit();
  }

  /** Parent template doc metadata (e.g. `Templates_notifications/{templateId}`). */
  upsertBotCarouselTemplateDoc(
    collection: string,
    idBOT: string,
    templateId: string,
    data: Record<string, unknown>,
    templatesSubcollection = 'Templates',
  ): Promise<void> {
    const path = `${collection}/${idBOT}/${templatesSubcollection}/${templateId}`;
    return this.afs
      .doc(path)
      .set(data, { merge: true })
      .catch((e) => {
        console.log('Something went wrong');
        throw e;
      });
  }

  getTemplateScenarioElements(
    collection: string,
    idBOT: string,
    templateId: string,
    scenarioId: string,
    templatesSubcollection = 'Templates',
  ): Observable<any[]> {
    const parentPath = `${collection}/${idBOT}/${templatesSubcollection}/${templateId}/scenarios/${scenarioId}`;
    return this.afs
      .collection<any>(`${parentPath}/elements`)
      .snapshotChanges()
      .pipe(
        map((actions) => {
          return actions
            .map((a) => {
              const data = a.payload.doc.data() as any;
              const id = a.payload.doc.id;
              const isEmpty = Object.keys(data).length === 0;
              const path = a.payload.doc.ref.path;
              return { id, path, isEmpty, ...data };
            })
            .sort((a, b) => ((a.index ?? 0) > (b.index ?? 0) ? 1 : -1));
        }),
      );
  }

  addTemplateScenarioElementDesign(
    collection: string,
    idBOT: string,
    templateId: string,
    scenarioId: string,
    element: any,
    templatesSubcollection = 'Templates',
  ): Promise<any> {
    let userID: string;
    try {
      userID = this.getRequiredUserId();
    } catch (error) {
      return Promise.reject(error);
    }
    const elementWithUserId = { ...element, userID };
    return this.afs
      .collection(
        `${collection}/${idBOT}/${templatesSubcollection}/${templateId}/scenarios/${scenarioId}/elements`,
      )
      .add(elementWithUserId)
      .then((docRef) => {
        return docRef;
      })
      .catch((e) => {
        console.log('Something went wrong');
        throw e;
      });
  }

  updateCarouselTemplateScenarioSchema(
    collection: string,
    idBOT: string,
    templateId: string,
    scenarioId: string,
    template: string | null,
    templatesSubcollection = 'Templates',
  ): Promise<void> {
    const path = `${collection}/${idBOT}/${templatesSubcollection}/${templateId}/scenarios/${scenarioId}`;
    const ref = this.afs.doc(path).ref;
    if (template === null || template === undefined) {
      return ref
        .update({ template: firebase.firestore.FieldValue.delete() })
        .catch(() => { console.log('Something went wrong'); return Promise.resolve(); });
    }
    return ref.set({ template }, { merge: true });
  }

  addTemplateElementDesign(
    collection: string,
    idBOT: string,
    templateId: string,
    element: any,
    templatesSubcollection = 'Templates',
  ): Promise<any> {
    let userID: string;
    try {
      userID = this.getRequiredUserId();
    } catch (error) {
      return Promise.reject(error);
    }
    const elementWithUserId = { ...element, userID };
    return this.afs
      .collection(`${collection}/${idBOT}/${templatesSubcollection}/`)
      .doc(templateId)
      .collection('elements')
      .add(elementWithUserId)
      .then((docRef) => {
        return docRef;
      })
      .catch((e) => {
        console.log('Something went wrong');
        throw e;
      });
  }

  addResponseAction(idBOT: string, recID: string, name: string): Promise<any> {
    return this.afs
      .collection(
        `${this.auth.isSuperAdmin() ? 'default-bots' : 'bots'}/${idBOT}/actions/`,
      )
      .doc(recID)
      .set({
        name: name,
        timeStamp: Date.now(),
      }) // this.afs.doc(docpath).update(element)
      .then(() => {
        //console.log('success');
        // this.snackBar.open('success', '', {
        // 	duration: 4000
        // });
      })
      .catch(() => { console.log('Something went wrong'); });
  }

  addResponseActionDefault(
    idBOT: string,
    recID: string,
    name: string,
    show: boolean,
  ): Promise<any> {
    return this.afs
      .collection(
        `${this.auth.isSuperAdmin() ? 'default-bots' : 'bots'}/${idBOT}/actions/`,
      )
      .doc(recID)
      .set({
        name: name,
        show: show,
        timeStamp: Date.now(),
      }) // this.afs.doc(docpath).update(element)
      .then(() => {
        //console.log('success');
        // this.snackBar.open('success', '', {
        // 	duration: 4000
        // });
      })
      .catch(() => { console.log('Something went wrong'); });
  }

  addResponseRecElement(
    idBOT: string,
    recID: string,
    name: string,
    element: any,
  ): Promise<any> {
    let userID: string;
    try {
      userID = this.getRequiredUserId();
    } catch (error) {
      return Promise.reject(error);
    }
    const elementWithUserId = { ...element, userID };
    return this.afs
      .collection(
        `${this.auth.isSuperAdmin() ? 'default-bots' : 'bots'}/${idBOT}/actions/`,
      )
      .doc(recID)
      .set({
        name: name,
        timeStamp: Date.now(),
        type: recID,
      })
      .then(() => {
        //TODO: add index into element, the order at which this element response will be shown @ bit's responses
        this.afs
          .collection(
            `${this.auth.isSuperAdmin() ? 'default-bots' : 'bots'}/${idBOT}/actions/`,
          )
          .doc(recID)
          .collection('elements')
          .add(elementWithUserId)
          .then(() => {
          })
          .catch(() => { console.log('Something went wrong'); });
      });
  }

  updateResponseRecElement(
    docpath: string,
    actionId: string,
    idBOT: string,
    id: string,
    element: any,
  ): Promise<any> {
    return this.afs
      .collection(
        `${this.auth.isSuperAdmin() ? 'default-bots' : 'bots'}/${idBOT}/actions/`,
      )
      .doc(actionId)
      .update({
        type: id,
      })
      .then(() => {
        this.afs
          .doc(docpath)
          .update(element)
          .then(() => {
          })
          .catch(() => { console.log('Something went wrong'); });
      });
  }

  updateRecMessage(user: any, type: any, message: any): Promise<any> {
    return this.afs
      .collection(
        `${this.auth.isSuperAdmin() ? 'default-bots' : 'bots'}/${user.pageID}/templates/`,
      )
      .doc('recommender')
      .set({
        messages: {
          [type]: message,
        },
      })

      .catch(() => { console.log('Something went wrong'); });
  }

  deleteResponseActionElement(docpath: string): Promise<any> {
    // Check if user is authenticated
    try {
      const userId = this.getRequiredUserId();
      if (!userId) {
        return Promise.reject(
          new Error('User must be authenticated to delete documents'),
        );
      }
    } catch (authError: any) {
      this.loggingService.error(
        'Authentication error in deleteResponseActionElement:',
        authError,
      );
      return Promise.reject(authError);
    }

    if (!docpath || typeof docpath !== 'string' || docpath.trim() === '') {
      const error = new Error('Invalid document path provided');
      this.loggingService.error(
        'Invalid path in deleteResponseActionElement:',
        docpath,
      );
      return Promise.reject(error);
    }

    return this.afs
      .doc(docpath)
      .delete()
      .then(() => {
      })
      .catch((e) => {
        console.log('Something went wrong');
        this.loggingService.error('Error deleting document:', e);

        if (
          e?.code === 'permission-denied' ||
          e?.message?.includes('Missing or insufficient permissions')
        ) {
        } else if (e?.code === 'not-found') {
        } else if (e?.code === 'unavailable') {
        } else {
        }

        return Promise.reject(e);
      });
  }
  deleteAllDocumentsInCollection(collectionPath: string): Promise<any> {
    return this.afs
      .collection(collectionPath)
      .get()
      .toPromise()
      .then((snapshot) => {
        const batch = this.afs.firestore.batch();
        snapshot?.forEach((doc) => {
          batch.delete(doc.ref);
        });

        return batch.commit().then(() => {
        });
      })
      .catch(() => { console.log('Something went wrong'); });
  }

  deleteResponseContent(docpath: string) {
    this.afs
      .doc(docpath)
      .get()
      .subscribe(
        (doc) => {
          if (doc.exists) {
            const data = doc.data() as Record<string, any>;
            const updates: { [key: string]: any } = {};

            // Iterate over each field and set it to FieldValue.delete()
            Object.keys(data).forEach((key) => {
              updates[key] = firebase.firestore.FieldValue.delete();
            });

            // Update the document with the deletions
            this.afs
              .doc(docpath)
              .update(updates)
              .then(() => {
              })
              .catch((error) => {
                this.notificationService.showError('Error updating document: ');
              });
          } else {
          }
        },
        (error) => {
          this.notificationService.showError('Error getting document: ');
        },
      );
  }
  deleteAction(
    idBOT: string,
    docpath: string,
    actionName: string,
  ): Promise<any> {
    return this.afs
      .doc(docpath)
      .delete()
      .then(() => {
        this.afs
          .collection(
            `${this.auth.isSuperAdmin() ? 'default-bots' : 'bots'}/${idBOT}/actions`,
          )
          .doc(`${actionName}`)
          .delete()
          .then(() => {
          })
          .catch(() => { console.log('Something went wrong'); });
      });
  }
  // to remoove
  getActions(idBOT: string): Observable<Actions[]> {
    return this.afs
      .collection<any>(
        `${this.auth.isSuperAdmin() ? 'default-bots' : 'bots'}/${idBOT}/actions`,
      )
      .snapshotChanges()
      .pipe(
        map((actions) =>
          actions.map((a) => {
            const data = a.payload.doc.data() as Actions;
            const docPath = a.payload.doc.ref.path;
            const id = a.payload.doc.id;

            return {
              id,
              docPath,
              ...data,
            };
          }),
        ),
      );
  }
  getActionsDesign(collection, idBOT): Observable<Actions[]> {
    return this.afs
      .collection<any>(`${collection}/${idBOT}/actions`)
      .snapshotChanges()
      .pipe(
        map((actions) =>
          actions.map((a) => {
            const data = a.payload.doc.data() as Actions;

            const id = a.payload.doc.id;
            let path1 = a.payload.doc.ref.path;
            return {
              id,
              path1,
              ...data,
            };
          }),
        ),
      );
  }

  getActionsList(idBOT: string): Observable<Actions[]> {
    return this.getCollectionWhere(
      [
        `${this.auth.isSuperAdmin() ? 'default-bots' : 'bots'}/${idBOT}/actions`,
      ],
      [
        {
          left: 'show',
          operator: '==',
          right: true,
        },
        {
          left: 'builtTemplate',
          operator: '==',
          right: false,
        },
      ],
    );
  }

  getActionsListDesign(botpath: string): Observable<Actions[]> {
    return this.getCollectionWhere(
      [`${botpath}/actions`],
      [
        {
          left: 'show',
          operator: '==',
          right: true,
        },
      ],
    );
  }

  getResponseActionsSpecial(
    collection: string,
    idBOT: string,
  ): Observable<Actions[]> {
    return this.getCollectionWhereWithalphabeticalOrder(
      [`${collection}/${idBOT}/actions`],
      [
        {
          left: 'special',
          operator: '==',
          right: true,
        },
      ],
    );
  }

  getResponseActionsSpecialForBot(
    collection: string,
    idBOT: string,
  ): Observable<Actions[]> {
    return this.getCollectionWhereWithalphabeticalOrder(
      [`${collection}/${idBOT}/actions`],
      [
        {
          left: 'show',
          operator: '==',
          right: true,
        },
        {
          left: 'special',
          operator: '==',
          right: true,
        },
      ],
    );
  }

  getBuiltActionsList(
    collection: string,
    idBOT: string,
  ): Observable<Actions[]> {
    return this.getCollectionWhere(
      [`${collection}/${idBOT}/actions`],
      [
        {
          left: 'show',
          operator: '==',
          right: true,
        },
        {
          left: 'built',
          operator: '==',
          right: true,
        },
      ],
    );
  }

  getTemplateActionsList(
    collection: string,
    idBOT: string,
  ): Observable<Actions[]> {
    return this.getCollectionWhere(
      [`${collection}/${idBOT}/actions`],
      [
        {
          left: 'orderStatus',
          operator: '==',
          right: true,
        },
        {
          left: 'show',
          operator: '==',
          right: false,
        },
      ],
    );
  }

  getOrderActionsList(
    collection: string,
    idBOT: string,
  ): Observable<Actions[]> {
    return this.getCollectionWhere(
      [`${collection}/${idBOT}/actions`],
      [
        {
          left: 'orderStatus',
          operator: '==',
          right: true,
        },
        {
          left: 'show',
          operator: '==',
          right: false,
        },
      ],
    );
  }

  getEventdActionsList(
    collection: string,
    idBOT: string,
  ): Observable<Actions[]> {
    return this.getCollectionWhere(
      [`${collection}/${idBOT}/actions`],
      [
        {
          left: 'show',
          operator: '==',
          right: false,
        },
        {
          left: 'event',
          operator: '==',
          right: true,
        },
      ],
    );
  }
  getCurrentDefaultAgentId(
    botId: string,
    isAdminLoggedAsUser: boolean,
  ): Observable<string | null> {
    if (!botId) {
      this.notificationService.showError('No botId provided to getCurrentDefaultAgentId');
      return of(null);
    }
    const collectionPath =
      this.auth.isSuperAdmin() && !isAdminLoggedAsUser
        ? 'default-bots'
        : 'bots';
    return this.afs
      .collection(collectionPath)
      .doc(botId)
      .collection('Agents', (ref) => ref.limit(1))
      .snapshotChanges()
      .pipe(
        take(1), // Only take first emission
        map((agents) => {
          if (agents && agents.length > 0) {
            const agentId = agents[0].payload.doc.id;
            return agentId;
          }
          return null;
        }),
        catchError((error) => {
          this.loggingService.error('[Firebase ERROR in getDocument()]', error);
          this.loggingService.error('Path attempted:', botId);
          return of(null);
        }),
      );
  }
  getResponseActionsList(
    collection: string,
    idBOT: string,
  ): Observable<Actions[]> {
    return this.getCollectionWhere(
      [`${collection}/${idBOT}/actions`],
      [
        {
          left: 'show',
          operator: '==',
          right: true,
        },
        {
          left: 'built',
          operator: '==',
          right: false,
        },
      ],
    );
  }
  loadFlowOnce(idBOT: string, flowId: string): Observable<any> {
    return this.afs
      .collection(`${this.auth.isSuperAdmin() ? 'default-bots' : 'bots'}`)
      .doc(idBOT)
      .collection('flows')
      .doc(flowId)
      .valueChanges()
      .pipe(take(1)); // âœ… Only take one emission
  }

  getFlow(
    botId: string,
    flowId: string,
    collectionName?: string,
  ): Observable<any> {
    const collection =
      collectionName ||
      (this.auth.isSuperAdmin() ? 'default-bots' : 'bots');
    return this.afs
      .collection(`${collection}/${botId}/flows`)
      .doc(flowId)
      .valueChanges()
      .pipe(take(1));
  }

  getBehaviorFlowByName(
    botId: string,
    behaviorName: string,
    collectionName?: string,
  ): Observable<any | null> {
    const collection = collectionName || this.auth.getBotsCollectionForSession();
    return this.afs
      .collection(`${collection}/${botId}/flows`, (ref) =>
        ref.where('isBehavior', '==', true).where('name', '==', behaviorName).limit(1),
      )
      .valueChanges({ idField: 'id' })
      .pipe(
        take(1),
        map((flows: any[]) => (flows?.length ? flows[0] : null)),
      );
  }

  /** True unless a Handover Flow document exists and is not Active. */
  isHandoverFlowActive(
    botId: string,
    collectionName?: string,
  ): Observable<boolean> {
    return this.getBehaviorFlowByName(botId, 'Handover Flow', collectionName).pipe(
      map((flow) => !flow || String(flow.status || '') === 'Active'),
      catchError(() => of(true)),
    );
  }

  /** Agent template tools toggled when a Core Behavior is activated/deactivated. */
  private readonly behaviorFunctionToolNames: Record<string, string[]> = {
    'Handover Flow': ['contact_support', 'handover_to_support', 'transfer_call'],
    'Order Status Flow': ['get_order_status'],
  };

  /**
   * Sets `disabled` on matching Agents/Templates.functions[] rows so the LLM
   * omits those tools while the behavior flow is inactive (Draft).
   */
  syncBehaviorFunctionToolsDisabled(
    botId: string,
    behaviorName: string,
    enabled: boolean,
    isAdminLoggedAsUser = false,
    collectionName?: string,
  ): Observable<void> {
    const toolNames = this.behaviorFunctionToolNames[String(behaviorName || '').trim()];
    if (!botId || !toolNames?.length) {
      return of(undefined);
    }

    const collection =
      collectionName ||
      (this.auth.isSuperAdmin() && !isAdminLoggedAsUser
        ? 'default-bots'
        : 'bots');

    return this.getCurrentDefaultAgentId(botId, isAdminLoggedAsUser).pipe(
      switchMap((agentId) => {
        if (!agentId) {
          return of(null);
        }
        return this.afs
          .collection(
            `${collection}/${botId}/Agents/${agentId}/Templates`,
            (ref) => ref.limit(1),
          )
          .snapshotChanges()
          .pipe(
            take(1),
            map((templates) => {
              if (!templates?.length) {
                return null;
              }
              return templates[0].payload.doc;
            }),
          );
      }),
      switchMap((templateDoc) => {
        if (!templateDoc) {
          return of(undefined);
        }
        const data = templateDoc.data() as { functions?: any[] };
        const functions = Array.isArray(data?.functions)
          ? [...data.functions]
          : [];
        if (!functions.length) {
          return of(undefined);
        }

        const toolNameSet = new Set(toolNames);
        let changed = false;
        const nextFunctions = functions.map((fn) => {
          if (!fn?.name || !toolNameSet.has(fn.name)) {
            return fn;
          }
          const shouldDisable = !enabled;
          if (shouldDisable) {
            if (fn.disabled === true) {
              return fn;
            }
            changed = true;
            return { ...fn, disabled: true };
          }
          if (fn.disabled !== true) {
            return fn;
          }
          changed = true;
          const { disabled, ...rest } = fn;
          return rest;
        });

        if (!changed) {
          return of(undefined);
        }

        return from(templateDoc.ref.update({ functions: nextFunctions })).pipe(
          map(() => undefined),
        );
      }),
      catchError((err) => {
        this.loggingService.error(
          'Failed to sync behavior function tool disabled flags',
          err,
        );
        return of(undefined);
      }),
    );
  }

  getFlowsForBot(botId: string): Observable<any[]> {
    return this.afs
      .collection(`${this.auth.isSuperAdmin() ? 'default-bots' : 'bots'}`)
      .doc(botId)
      .collection('flows')
      .valueChanges({ idField: 'id' });
  }
  createFlow(botId: string, collection: string, dataToSave: any): Promise<any> {
    let userID: string;
    try {
      userID = this.getRequiredUserId();
    } catch (error) {
      return Promise.reject(error);
    }
    const flowDataWithUserId = { ...dataToSave, userID };
    return this.afs
      .collection(collection)
      .doc(botId)
      .collection('flows')
      .add(flowDataWithUserId)
      .then((docRef) => {
        const createdFlow = { id: docRef.id, ...flowDataWithUserId };
        return createdFlow;
      })
      .catch((err) => {
        this.notificationService.showError('Error creating flow:');
        throw err;
      });
  }
  createDefaultFlows(
    botId: string,
    userId: string,
    botType?: string,
  ): Observable<void> {
    const isCallbot = botType === 'voice-phone';
    const flowsToCreate = [
      {
        name: 'Welcome Message',
        description: 'Sends a greeting at the start of a new conversation. Covers proactive, returning user, and re-engagement scenarios.',
        creator: this.createWelcomeScenarios.bind(this),
      },
      {
        name: 'Fallback Response',
        description: isCallbot
          ? 'Handles misunderstandings, policy violations, and silence (No Reply).'
          : 'Triggers when the bot cannot understand a request or respond.',
        creator: (botId: string, flowId: string) =>
          this.createFallbackScenarios(botId, flowId, isCallbot),
      },
      ...(isCallbot
        ? []
        : [
            {
              name: 'Bot Invocation Response',
              description:
                'Triggered when a user tries to interact with a deactivated bot.',
              creator: this.createBotInvocationScenarios.bind(this),
            },
          ]),
      ...(isCallbot
        ? [
            {
              name: 'End Conversation',
              description:
                'Handles call termination when the task is completed, silence timeout is reached, or the maximum call duration is exceeded.',
              creator: this.createEndConversationScenarios.bind(this),
            },
            {
              name: 'Error',
              description:
                'Handles API/tool failures and fatal system errors. Visible and editable by super admins only.',
              creator: this.createErrorScenarios.bind(this),
            },
          ]
        : []),
    ];

    const creationObservables = flowsToCreate.map((flowData) => {
      const newFlow = {
        name: flowData.name,
        type: 'Custom Flow',
        description: flowData.description,
        createdAt: new Date(),
        createdBy: userId,
        status: 'Draft',
        isBehavior: true,
      };

      return from(
        this.afs
          .collection(`${this.auth.isSuperAdmin() ? 'default-bots' : 'bots'}`)
          .doc(botId)
          .collection('flows')
          .add(newFlow),
      ).pipe(
        switchMap((docRef) => {
          return flowData.creator(botId, docRef.id).pipe(map(() => docRef.id));
        }),
        catchError((err) => {
          this.loggingService.error('[Firebase ERROR in getDocument()]', err);
          this.loggingService.error('Path attempted:', botId);
          return of(null);
        }),
      );
    });

    return forkJoin(creationObservables).pipe(
      map(() => {
        return void 0;
      }),
    );
  }

  createFixedMessages(
    botId: string,
    botType?: string,
    userId?: string,
    botLanguage?: string,
    collection?: string,
  ): Observable<void> {
    const resolvedCollection =
      collection ||
      (this.auth.isSuperAdmin() ? 'default-bots' : 'bots');
    const isCallbot = botType === 'voice-phone';
    const normalizedBotLang = botLanguage?.split('-')[0]?.toLowerCase();
    const sourceLanguage = normalizedBotLang || (isCallbot ? 'fr' : 'en');

    const defaults = FIXED_MESSAGE_DEFAULTS.map((def) => {
      const source =
        def.key === 'fatal_error'
          ? this.fatalErrorInstruction
          : getFixedMessageDefaultSource(def, sourceLanguage);
      return { ...def, source };
    });

    return this.listBotFixedMessages(botId, resolvedCollection).pipe(
      take(1),
      switchMap((existing) => {
        const existingKeys = new Set(
          (existing || []).map((msg) => msg.key).filter(Boolean),
        );
        const batch = this.afs.firestore.batch();
        let pendingWrites = 0;

        for (const def of defaults) {
          if (existingKeys.has(def.key)) {
            continue;
          }

          const ref = this.afs
            .collection(`${resolvedCollection}/${botId}/Messages`)
            .doc(def.key).ref;
          batch.set(
            ref,
            {
              key: def.key,
              name: def.name,
              description: def.description,
              event: 'status',
              source: def.source,
              sourceLanguage,
              translations: { [sourceLanguage.split('-')[0]]: def.source },
              priority: def.priority,
              updatedAt: Date.now(),
              ...(userId ? { userID: userId } : {}),
            },
            { merge: true },
          );
          pendingWrites += 1;
        }

        if (pendingWrites === 0) {
          return of(void 0);
        }

        return from(batch.commit()).pipe(
                    map(() => void 0),
        );
      }),
      catchError((err) => {
        this.loggingService.error('[Firebase ERROR creating fixed messages]', err);
        return of(void 0);
      }),
    );
  }

  listBotFixedMessages(
    botId: string,
    collection?: string,
  ): Observable<
    {
      id?: string;
      key: string;
      name: string;
      description?: string;
      event: string;
      source?: string;
      sourceLanguage?: string;
      translations?: Record<string, string>;
      priority?: number;
      updatedAt?: number;
    }[]
  > {
    if (!botId) {
      return of([]);
    }
    const resolvedCollection =
      collection || this.auth.getBotsCollectionForSession();
    return this.afs
      .collection(`${resolvedCollection}/${botId}/Messages`)
      .valueChanges({ idField: 'id' })
      .pipe(
        take(1),
        map((docs) =>
          (docs || []).map((doc: any) => ({
            ...doc,
            key: doc.key || doc.id,
            event: doc.event || 'status',
          })),
        ),
        catchError((err) => {
          this.loggingService.error('[listBotFixedMessages]', err);
          return of([]);
        }),
      );
  }

  /** StandBy copy at {collection}/{botId}/Messages/standby, keyed by language. */
  getStandbyTranslations(
    botId: string,
    collection?: string,
  ): Observable<Record<string, string>> {
    if (!botId) {
      return of({});
    }
    const resolvedCollection =
      collection || this.auth.getBotsCollectionForSession();
    return from(
      this.afs
        .doc(`${resolvedCollection}/${botId}/Messages/standby`)
        .ref.get(),
    ).pipe(
      map((snap) => {
        const data = (snap.data() || {}) as { translations?: Record<string, string> };
        const translations = data.translations;
        if (!translations || typeof translations !== 'object') {
          return {};
        }
        return Object.keys(translations).reduce((acc, key) => {
          const value = translations[key];
          if (typeof value === 'string' && value.trim()) {
            acc[key.split('-')[0].toLowerCase()] = value.trim();
          }
          return acc;
        }, {} as Record<string, string>);
      }),
      catchError((err) => {
        this.loggingService.error('[getStandbyTranslations]', err);
        return of({});
      }),
    );
  }

  upsertStandbyMessage(
    botId: string,
    lang: string,
    text: string,
    defaults: Record<string, string>,
    collection?: string,
  ): Observable<void> {
    const code = String(lang || 'en').split('-')[0].toLowerCase();
    const value = String(text || '').trim();
    if (!botId || !code || !value) {
      return throwError(() => new Error('Missing standby message fields'));
    }
    const resolvedCollection =
      collection || this.auth.getBotsCollectionForSession();
    const path = `${resolvedCollection}/${botId}/Messages/standby`;
    return from(
      (async () => {
        const snap = await this.afs.doc(path).ref.get();
        const existing = (snap.data() || {}) as {
          sourceLanguage?: string;
          translations?: Record<string, string>;
        };
        const sourceLanguage = String(existing.sourceLanguage || 'en')
          .split('-')[0]
          .toLowerCase();
        const translations = {
          ...defaults,
          ...(existing.translations || {}),
          [code]: value,
        };
        await this.afs.doc(path).set(
          {
            key: 'standby',
            name: 'StandBy message',
            description:
              'Shown when the bot is in StandBy mode (BotMode is off).',
            event: 'standby',
            source: translations[sourceLanguage] || translations['en'] || value,
            sourceLanguage,
            translations,
            updatedAt: Date.now(),
          },
          { merge: true },
        );
      })(),
    ).pipe(
      map(() => void 0),
      catchError((err) => {
        this.loggingService.error('[upsertStandbyMessage]', err);
        return throwError(() => err);
      }),
    );
  }

  saveBotFixedMessage(
    botId: string,
    key: string,
    source: string,
    sourceLanguage: string,
    collection?: string,
    userId?: string,
  ): Observable<void> {
    if (!botId || !key) {
      return of(void 0);
    }
    const resolvedCollection =
      collection || this.auth.getBotsCollectionForSession();
    const def = FIXED_MESSAGE_DEFAULTS.find((d) => d.key === key);
    const payload: Record<string, unknown> = {
      key,
      name: def?.name || key,
      description: def?.description || '',
      event: 'status',
      source,
      sourceLanguage,
      translations: { [sourceLanguage.split('-')[0]]: source },
      priority: def?.priority ?? 999,
      updatedAt: Date.now(),
    };
    if (userId) {
      payload['userID'] = userId;
    }
    return from(
      this.afs
        .collection(`${resolvedCollection}/${botId}/Messages`)
        .doc(key)
        .set(payload, { merge: true }),
    ).pipe(
      map(() => void 0),
      catchError((err) => {
        this.loggingService.error('[saveBotFixedMessage]', err);
        return throwError(() => err);
      }),
    );
  }

  deleteBotFixedMessage(
    botId: string,
    key: string,
    collection?: string,
  ): Observable<void> {
    if (!botId || !key) {
      return of(void 0);
    }
    const resolvedCollection =
      collection || this.auth.getBotsCollectionForSession();
    return from(
      this.afs
        .collection(`${resolvedCollection}/${botId}/Messages`)
        .doc(key)
        .delete(),
    ).pipe(
      map(() => void 0),
      catchError((err) => {
        this.loggingService.error('[deleteBotFixedMessage]', err);
        return throwError(() => err);
      }),
    );
  }

  tombstoneBotFixedMessage(
    botId: string,
    key: string,
    collection?: string,
  ): Observable<void> {
    if (!botId || !key) {
      return of(void 0);
    }
    const resolvedCollection =
      collection || this.auth.getBotsCollectionForSession();
    return from(
      this.afs
        .collection(`${resolvedCollection}/${botId}/Messages`)
        .doc(key)
        .set({
          key,
          event: 'status',
          deleted: true,
          updatedAt: Date.now(),
        }),
    ).pipe(
      map(() => void 0),
      catchError((err) => {
        this.loggingService.error('[tombstoneBotFixedMessage]', err);
        return throwError(() => err);
      }),
    );
  }

  // ----------------------------------------------------------------------
  // Welcome Scenarios
  // ----------------------------------------------------------------------
  createWelcomeScenarios(botId: string, flowId: string): Observable<void> {
    const flowRef = this.afs
      .collection(`${this.auth.isSuperAdmin() ? 'default-bots' : 'bots'}`)
      .doc(botId)
      .collection('flows')
      .doc(flowId).ref;

    const scenarios = [
      /* ...your scenarios... */
    ];

    // Use update instead of set to avoid overwriting the document
    return from(flowRef.update({ scenarios })).pipe(
            catchError((err) => {
        this.loggingService.error('[Firebase ERROR in getDocument()]', err);
        this.loggingService.error('Path attempted:', flowId);
        return of(void 0);
      }),
    );
  }

  // ----------------------------------------------------------------------
  // Fallback Scenarios
  // ----------------------------------------------------------------------
  private readonly offTopicRequestInstruction =
    "Generate a polite message when the user's request is outside the assistant's primary scope or domain. Clearly state that this topic falls outside the scope of what you can assist with and suggest an alternative way to obtain help, such as contacting a live agent.";

  private readonly noReplyInstruction =
    'The user has not responded after a period of silence. Call Manager has triggered this scenario to re-engage the caller. Politely check whether the user is still on the line and invite them to continue the conversation. Keep the message brief, friendly, and non-intrusive.';

  private readonly conversationEndedInstruction =
    'The caller has clearly ended the conversation (said goodbye / thanks) but may not have hung up. Warmly acknowledge and say a brief farewell before the call ends. One short TTS sentence.';

  private readonly apiToolErrorGeneralInstruction =
    "A tool/function call failed (you receive status/code/message). Reply to the caller without exposing the raw error or any internal detail. If it indicates missing/invalid input, ask the caller for it so it can be retried; otherwise briefly apologize and offer to retry or hand off to a human. One short spoken sentence, in the caller's language.";

  private readonly fatalErrorInstruction =
    'Sorry, we\'re temporarily unable to respond right now (for example due to a system issue or plan limit reached). Please try again later.';

  private readonly callbotFatalErrorInstruction =
    'Sorry, we\'re temporarily unable to respond. Please try again later.';

  /**
   * First (and intermediate) knowledge gaps: admit the miss only. Handover is
   * handled by {@link informationNotFoundHandoverInstruction} once thresholds
   * are reached.
   *
   * Keep in sync with `PLAIN_GAP_AI_INSTRUCTION` in kbGapHandover.service.js.
   */
  private readonly informationNotFoundInstruction =
    'Generate a friendly and helpful message when the user’s request is understood, but no relevant information is found. Clearly explain that you couldn’t find the requested information.';

  /**
   * Reached only after repeated unanswered questions, so the message both admits
   * the gap and offers a human. Naming the subject is what makes the offer
   * credible: "I have no information about delivery to Corsica" reads very
   * differently from "I do not have the answer".
   */
  private readonly informationNotFoundHandoverInstruction =
    'You have now failed to answer the user several times in a row. Acknowledge the specific subject you could not find information about, naming it explicitly rather than saying only that you do not know. Then offer to connect them with a human colleague who can help. Phrase it as a genuine offer they are free to decline, not as an action you have already taken, and ask whether they would like you to do it. Keep it to two short sentences and reply in the user\'s language.';

  createFallbackScenarios(
    botId: string,
    flowId: string,
    isCallbot = false,
  ): Observable<void> {
    const collection = this.auth.isSuperAdmin() ? 'default-bots' : 'bots';
    const batch = this.afs.firestore.batch();
    const flowRef = this.afs
      .collection(collection)
      .doc(botId)
      .collection('flows')
      .doc(flowId).ref;

    const scenarios: any[] = [
      {
        conditions: [
          { field: 'User Not Understood', operator: 'is', value: 'true' },
        ],
        id: 'PAYLOAD_USER_NOT_UNDERSTOOD',
        isDefault: true,
        name: 'User Not Understood',
        priority: 1,
        status: 'Active',
      },
      // Must precede the plain Information Not Found scenario: the backend
      // evaluates non-default scenarios in array order and returns the first
      // whose conditions all match, so a plain match placed first would always
      // win and the offer would never be reached.
      {
        conditions: [
          { field: 'Information Not Found', operator: 'is', value: 'true' },
          {
            field: 'Knowledge Gap Handover Offer',
            operator: 'is',
            value: 'true',
          },
        ],
        id: 'PAYLOAD_INFORMATION_NOT_FOUND_HANDOVER',
        isDefault: false,
        name: 'Information Not Found - Offer Human',
        priority: 2,
        response: 'PAYLOAD_INFORMATION_NOT_FOUND_HANDOVER',
        status: 'Active',
      },
      {
        conditions: [
          { field: 'Information Not Found', operator: 'is', value: 'true' },
        ],
        id: 'PAYLOAD_INFORMATION_NOT_FOUND',
        isDefault: false,
        name: 'Information Not Found',
        priority: 2,
        status: 'Active',
      },
      {
        conditions: [
          { field: 'Off-Topic Request', operator: 'is', value: 'true' },
        ],
        id: 'PAYLOAD_OFF_TOPIC_REQUEST',
        isDefault: false,
        name: 'Off-Topic Request',
        priority: 3,
        response: 'PAYLOAD_OFF_TOPIC_REQUEST',
        status: 'Active',
      },
      {
        conditions: [
          {
            field: 'Security & Policy Violation',
            operator: 'is',
            value: 'true',
          },
        ],
        id: 'PAYLOAD_SECURITY_VIOLATION',
        isDefault: false,
        name: 'Security & Policy Violation',
        priority: 4,
        status: 'Active',
      },
    ];

    if (!isCallbot) {
      scenarios.push({
        conditions: [
          { field: 'API / Tool Error', operator: 'is', value: 'true' },
        ],
        id: 'PAYLOAD_API_TOOL_ERROR',
        isDefault: false,
        name: 'Fallback Type: API / Tool Error',
        priority: 5,
        status: 'Active',
      });
    }

    if (isCallbot) {
      let userID: string;
      try {
        userID = this.getRequiredUserId();
      } catch (error) {
        return throwError(() => error);
      }

      scenarios.push({
        conditions: [{ field: 'No Reply', operator: 'is', value: 'true' }],
        id: 'PAYLOAD_NO_REPLY',
        isDefault: false,
        name: 'No Reply',
        priority: isCallbot ? 5 : 6,
        response: 'PAYLOAD_NO_REPLY',
        status: 'Active',
      });

      const actionRef = this.afs
        .collection(`${collection}/${botId}/actions`)
        .doc('PAYLOAD_NO_REPLY').ref;
      batch.set(
        actionRef,
        {
          name: 'No Reply',
          show: true,
          built: false,
          builtTemplate: false,
          event: false,
          intention: false,
          isAiInstruction: true,
          aiInstruction: this.noReplyInstruction,
          flowIds: [flowId],
          userID,
          timeStamp: Date.now(),
        },
        { merge: true },
      );
    }

    const plainGapActionRef = this.afs
      .collection(`${collection}/${botId}/actions`)
      .doc('PAYLOAD_INFORMATION_NOT_FOUND').ref;
    batch.set(
      plainGapActionRef,
      {
        name: 'Information Not Found',
        show: true,
        built: false,
        builtTemplate: false,
        event: false,
        intention: false,
        isAiInstruction: true,
        aiInstruction: this.informationNotFoundInstruction,
        flowIds: [flowId],
        timeStamp: Date.now(),
      },
      { merge: true },
    );

    const gapHandoverActionRef = this.afs
      .collection(`${collection}/${botId}/actions`)
      .doc('PAYLOAD_INFORMATION_NOT_FOUND_HANDOVER').ref;
    batch.set(
      gapHandoverActionRef,
      {
        name: 'Information Not Found - Offer Human',
        show: true,
        built: false,
        builtTemplate: false,
        event: false,
        intention: false,
        isAiInstruction: true,
        aiInstruction: this.informationNotFoundHandoverInstruction,
        flowIds: [flowId],
        timeStamp: Date.now(),
      },
      { merge: true },
    );

    batch.update(flowRef, { scenarios });

    return from(batch.commit()).pipe(
      switchMap(() => this.ensureOffTopicRequestInstruction(botId, flowId)),
            map(() => void 0),
      catchError((err) => {
        this.loggingService.error('[Firebase ERROR in getDocument()]', err);
        this.loggingService.error('Path attempted:', flowId);
        return of(void 0);
      }),
    );
  }

  ensureOffTopicRequestInstruction(
    botId: string,
    flowId: string,
  ): Observable<boolean> {
    const collection = this.auth.isSuperAdmin() ? 'default-bots' : 'bots';
    const actionId = 'PAYLOAD_OFF_TOPIC_REQUEST';
    let userID: string;
    try {
      userID = this.getRequiredUserId();
    } catch (error) {
      return throwError(() => error);
    }

    const flowRef = this.afs
      .collection(collection)
      .doc(botId)
      .collection('flows')
      .doc(flowId);
    const actionRef = this.afs
      .collection(`${collection}/${botId}/actions`)
      .doc(actionId);

    return from(actionRef.ref.get()).pipe(
      switchMap((actionDoc) => {
        const existingInstruction = (
          actionDoc.data()?.['aiInstruction'] as string | undefined
        )?.trim();
        if (existingInstruction) {
          return of(false);
        }

        return from(flowRef.ref.get()).pipe(
          switchMap((flowDoc) => {
            const scenarios = ((flowDoc.data()?.['scenarios'] as any[]) || []).map(
              (scenario) => {
                if (
                  scenario.id === actionId ||
                  scenario.name === 'Off-Topic Request'
                ) {
                  return { ...scenario, response: actionId };
                }
                return scenario;
              },
            );

            const batch = this.afs.firestore.batch();
            batch.set(
              actionRef.ref,
              {
                name: 'Off-Topic Request',
                show: true,
                built: false,
                builtTemplate: false,
                event: false,
                intention: false,
                isAiInstruction: true,
                aiInstruction: this.offTopicRequestInstruction,
                flowIds: [flowId],
                userID,
                timeStamp: Date.now(),
              },
              { merge: true },
            );
            batch.update(flowRef.ref, { scenarios });

            return from(batch.commit()).pipe(map(() => true));
          }),
        );
      }),
      catchError((err) => {
        this.loggingService.error('[ensureOffTopicRequestInstruction]', err);
        return of(false);
      }),
    );
  }

  /**
   * Ensures the gap-handover scenario and action exist on Fallback Response.
   *
   * Backfill for every bot created before the offer existed. The scenario is
   * inserted immediately before the plain Information Not Found scenario rather
   * than appended, because the backend matches non-default scenarios in array
   * order.
   */
  ensureGapHandoverFallbackScenario(
    botId: string,
    flowId: string,
  ): Observable<boolean> {
    const collection = this.auth.isSuperAdmin() ? 'default-bots' : 'bots';
    const actionId = 'PAYLOAD_INFORMATION_NOT_FOUND_HANDOVER';
    const scenarioName = 'Information Not Found - Offer Human';
    let userID: string;
    try {
      userID = this.getRequiredUserId();
    } catch (error) {
      return throwError(() => error);
    }

    const flowRef = this.afs
      .collection(collection)
      .doc(botId)
      .collection('flows')
      .doc(flowId);
    const actionRef = this.afs
      .collection(`${collection}/${botId}/actions`)
      .doc(actionId);

    return from(flowRef.ref.get()).pipe(
      switchMap((flowDoc) => {
        const scenarios = (flowDoc.data()?.['scenarios'] as any[]) || [];
        const alreadyPresent = scenarios.some(
          (scenario) =>
            scenario?.id === actionId || scenario?.name === scenarioName,
        );
        if (alreadyPresent) {
          return of(false);
        }

        const gapIndex = scenarios.findIndex(
          (scenario) =>
            scenario?.id === 'PAYLOAD_INFORMATION_NOT_FOUND' ||
            scenario?.name === 'Information Not Found',
        );
        // Without a gap scenario to precede there is nothing to switch away
        // from, so leave the flow untouched instead of adding an offer that
        // could never be reached.
        if (gapIndex === -1) {
          return of(false);
        }

        const newScenario = {
          conditions: [
            { field: 'Information Not Found', operator: 'is', value: 'true' },
            {
              field: 'Knowledge Gap Handover Offer',
              operator: 'is',
              value: 'true',
            },
          ],
          id: actionId,
          isDefault: false,
          name: scenarioName,
          priority: 2,
          response: actionId,
          status: 'Active',
        };
        const updatedScenarios = [
          ...scenarios.slice(0, gapIndex),
          newScenario,
          ...scenarios.slice(gapIndex),
        ];

        const batch = this.afs.firestore.batch();
        batch.set(
          actionRef.ref,
          {
            name: scenarioName,
            show: true,
            built: false,
            builtTemplate: false,
            event: false,
            intention: false,
            isAiInstruction: true,
            aiInstruction: this.informationNotFoundHandoverInstruction,
            flowIds: [flowId],
            userID,
            timeStamp: Date.now(),
          },
          { merge: true },
        );
        batch.update(flowRef.ref, { scenarios: updatedScenarios });

        return from(batch.commit()).pipe(map(() => true));
      }),
      catchError((err) => {
        this.loggingService.error('[ensureGapHandoverFallbackScenario]', err);
        return of(false);
      }),
    );
  }

  /** Ensures No Reply scenario and action exist on Fallback Response (callbot only). */
  ensureNoReplyFallbackScenario(
    botId: string,
    flowId: string,
  ): Observable<boolean> {
    const collection = this.auth.isSuperAdmin() ? 'default-bots' : 'bots';
    const actionId = 'PAYLOAD_NO_REPLY';
    let userID: string;
    try {
      userID = this.getRequiredUserId();
    } catch (error) {
      return throwError(() => error);
    }

    const flowRef = this.afs
      .collection(collection)
      .doc(botId)
      .collection('flows')
      .doc(flowId);
    const actionRef = this.afs
      .collection(`${collection}/${botId}/actions`)
      .doc(actionId);

    return from(flowRef.ref.get()).pipe(
      switchMap((flowDoc) => {
        const scenarios = (flowDoc.data()?.['scenarios'] as any[]) || [];
        const hasNoReply = scenarios.some(
          (scenario) =>
            scenario.id === actionId || scenario.name === 'No Reply',
        );
        if (hasNoReply) {
          return of(false);
        }

        const updatedScenarios = [
          ...scenarios,
          {
            conditions: [{ field: 'No Reply', operator: 'is', value: 'true' }],
            id: actionId,
            isDefault: false,
            name: 'No Reply',
            priority: 6,
            response: actionId,
            status: 'Active',
          },
        ];

        const batch = this.afs.firestore.batch();
        batch.set(
          actionRef.ref,
          {
            name: 'No Reply',
            show: true,
            built: false,
            builtTemplate: false,
            event: false,
            intention: false,
            isAiInstruction: true,
            aiInstruction: this.noReplyInstruction,
            flowIds: [flowId],
            userID,
            timeStamp: Date.now(),
          },
          { merge: true },
        );
        batch.update(flowRef.ref, { scenarios: updatedScenarios });

        return from(batch.commit()).pipe(map(() => true));
      }),
      catchError((err) => {
        this.loggingService.error('[ensureNoReplyFallbackScenario]', err);
        return of(false);
      }),
    );
  }

  /** Ensures Fatal Error scenario/action exist on Fallback Response (non-callbot channels). */
  ensureFatalErrorFallbackScenario(
    botId: string,
    flowId: string,
    isCallbot = false,
  ): Observable<boolean> {
    const collection = this.auth.isSuperAdmin() ? 'default-bots' : 'bots';
    const actionId = 'PAYLOAD_FATAL_ERROR';
    let userID: string;
    try {
      userID = this.getRequiredUserId();
    } catch (error) {
      return throwError(() => error);
    }

    const flowRef = this.afs
      .collection(collection)
      .doc(botId)
      .collection('flows')
      .doc(flowId);
    const actionRef = this.afs
      .collection(`${collection}/${botId}/actions`)
      .doc(actionId);

    return from(flowRef.ref.get()).pipe(
      switchMap((flowDoc) => {
        const scenarios = (flowDoc.data()?.['scenarios'] as any[]) || [];
        const existingIndex = scenarios.findIndex(
          (scenario) => scenario.id === actionId || scenario.name === 'FATAL_ERROR',
        );
        const maxPriority = scenarios.reduce((max, scenario) => {
          const value = Number(scenario?.priority);
          return Number.isFinite(value) ? Math.max(max, value) : max;
        }, 0);

        const fatalScenario = {
          conditions: [{ field: 'Fatal Error', operator: 'is', value: 'true' }],
          id: actionId,
          isDefault: false,
          name: 'FATAL_ERROR',
          priority: maxPriority + 1,
          response: actionId,
          status: 'Active',
        };

        const updatedScenarios =
          existingIndex >= 0
            ? scenarios.map((scenario, index) =>
                index === existingIndex
                  ? {
                      ...scenario,
                      ...fatalScenario,
                      priority: scenario?.priority ?? fatalScenario.priority,
                    }
                  : scenario,
              )
            : [...scenarios, fatalScenario];

        const batch = this.afs.firestore.batch();
        batch.set(
          actionRef.ref,
          {
            name: 'FATAL_ERROR',
            show: true,
            built: false,
            builtTemplate: false,
            event: false,
            intention: false,
            isAiInstruction: true,
            aiInstruction: isCallbot
              ? this.callbotFatalErrorInstruction
              : this.fatalErrorInstruction,
            flowIds: [flowId],
            userID,
            timeStamp: Date.now(),
          },
          { merge: true },
        );
        batch.update(flowRef.ref, { scenarios: updatedScenarios });

        return from(batch.commit()).pipe(map(() => true));
      }),
      catchError((err) => {
        this.loggingService.error('[ensureFatalErrorFallbackScenario]', err);
        return of(false);
      }),
    );
  }

  // ----------------------------------------------------------------------
  // Bot Invocation Scenarios
  // ----------------------------------------------------------------------
  createBotInvocationScenarios(
    botId: string,
    flowId: string,
  ): Observable<void> {
    const collection = this.auth.isSuperAdmin() ? 'default-bots' : 'bots';
    return this.ensureBotInvocationResponseAction(botId, flowId, collection).pipe(
      map(() => void 0),
      catchError((err) => {
        this.loggingService.error('[createBotInvocationScenarios]', err);
        return of(void 0);
      }),
    );
  }

  /** Ensures Bot Invocation Response scenario + PAYLOAD_DISABLED_BOT_SUMMONED action exist. */
  ensureBotInvocationResponseAction(
    botId: string,
    flowId: string,
    collectionName?: string,
  ): Observable<boolean> {
    const collection =
      collectionName || this.auth.getBotsCollectionForSession();
    const actionId = 'PAYLOAD_DISABLED_BOT_SUMMONED';
    let userID: string;
    try {
      userID = this.getRequiredUserId();
    } catch (error) {
      return throwError(() => error);
    }

    const flowRef = this.afs
      .collection(collection)
      .doc(botId)
      .collection('flows')
      .doc(flowId);
    const actionRef = this.afs
      .collection(`${collection}/${botId}/actions`)
      .doc(actionId);

    return forkJoin([
      from(flowRef.ref.get()),
      from(actionRef.ref.get()),
    ]).pipe(
      switchMap(([flowDoc, actionDoc]) => {
        const scenarios = (flowDoc.data()?.['scenarios'] as any[]) || [];
        const hasScenario = scenarios.some(
          (scenario) =>
            scenario.id === actionId ||
            scenario.response === actionId ||
            scenario.name === 'Bot Invoked While Deactivated' ||
            scenario.name === 'Bot Summoned While Disabled',
        );
        const actionData = actionDoc.data() as Record<string, unknown> | undefined;
        const matchesLibraryQuery =
          actionDoc.exists &&
          actionData?.['show'] === true &&
          actionData?.['built'] === false &&
          actionData?.['builtTemplate'] === false &&
          actionData?.['event'] === false &&
          actionData?.['intention'] === false &&
          actionData?.['timeStamp'] != null;
        const needsClassifier = !String(
          actionData?.['classifierInstruction'] || '',
        ).trim();

        if (matchesLibraryQuery && !needsClassifier && hasScenario) {
          return of(false);
        }

        const batch = this.afs.firestore.batch();
        const flowIds = Array.from(
          new Set([
            ...((actionData?.['flowIds'] as string[]) || []),
            flowId,
          ]),
        );

        batch.set(
          actionRef.ref,
          {
            name: 'Bot Invoked While Deactivated',
            show: true,
            built: false,
            builtTemplate: false,
            event: false,
            intention: false,
            isAiInstruction: true,
            classifierInstruction: BOT_INVOCATION_CLASSIFIER_INSTRUCTION,
            aiInstruction:
              'The user invoked you while the bot was deactivated. Answer their latest message helpfully.',
            flowIds,
            userID,
            timeStamp: Date.now(),
          },
          { merge: true },
        );

        if (!hasScenario) {
          const updatedScenarios = [
            ...scenarios,
            {
              conditions: [
                { field: 'Bot Mode', operator: 'is', value: 'Standby' },
              ],
              id: actionId,
              isDefault: true,
              name: 'Bot Invoked While Deactivated',
              priority: 1,
              response: actionId,
              status: 'Active',
              description: resolveScenarioDescription(
                'Bot Invocation Response',
                'Bot Invoked While Deactivated',
              ),
            },
          ];
          batch.update(flowRef.ref, { scenarios: updatedScenarios });
        } else {
          const updatedScenarios = scenarios.map((scenario) => {
            const isTarget =
              scenario.id === actionId ||
              scenario.response === actionId ||
              scenario.name === 'Bot Invoked While Deactivated' ||
              scenario.name === 'Bot Summoned While Disabled';
            if (!isTarget) {
              return scenario;
            }
            return {
              ...scenario,
              id: actionId,
              response: actionId,
              name: 'Bot Invoked While Deactivated',
            };
          });
          batch.update(flowRef.ref, { scenarios: updatedScenarios });
        }

        return from(batch.commit()).pipe(map(() => true));
      }),
      catchError((err) => {
        this.loggingService.error('[ensureBotInvocationResponseAction]', err);
        return of(false);
      }),
    );
  }

  // End Conversation Scenarios (voice-phone only)
  createEndConversationScenarios(
    botId: string,
    flowId: string,
  ): Observable<void> {
    const collection = this.auth.isSuperAdmin() ? 'default-bots' : 'bots';
    let userID: string;
    try {
      userID = this.getRequiredUserId();
    } catch (error) {
      return throwError(() => error);
    }

    const scenarioDefs = [
      {
        id: 'PAYLOAD_CONVERSATION_ENDED',
        name: 'CONVERSATION_ENDED',
        priority: 1,
        isDefault: false,
        conditions: [{ field: 'End Call Reason', operator: 'is', value: 'goodbye' }],
        instruction: this.conversationEndedInstruction,
      },
      {
        id: 'PAYLOAD_TASK_COMPLETED',
        name: 'TASK_COMPLETED',
        priority: 2,
        isDefault: true,
        conditions: [{ field: 'End Call Reason', operator: 'is', value: 'task_completed' }],
        instruction:
          'The conversation has reached a natural conclusion. The user has indicated they are finished (e.g., goodbye, thank you, no further assistance needed), but the call is still active. Politely thank the user, confirm that the session is ending, and inform them that the call will now be disconnected. Then trigger the end_call function.',
      },
      {
        id: 'PAYLOAD_NO_INPUT_EXHAUSTED',
        name: 'NO_INPUT_EXHAUSTED',
        priority: 3,
        isDefault: false,
        conditions: [{ field: 'End Call Reason', operator: 'is', value: 'no_input_exhausted' }],
        instruction:
          'The user has not responded after all configured no-input re-prompts have been exhausted. Assume the caller is unavailable or has left the call. Inform the user that no response was detected and that the call will now be disconnected. Then proceed with hangup.',
      },
      {
        id: 'PAYLOAD_MAX_DURATION_REACHED',
        name: 'MAX_DURATION_REACHED',
        priority: 4,
        isDefault: false,
        conditions: [{ field: 'End Call Reason', operator: 'is', value: 'max_duration_reached' }],
        instruction:
          'The maximum allowed call duration has been reached. Politely inform the user that the session time limit has been reached and that the call must now end. Thank them for their time and proceed with hangup.',
      },
    ];

    const batch = this.afs.firestore.batch();
    const flowRef = this.afs
      .collection(collection)
      .doc(botId)
      .collection('flows')
      .doc(flowId).ref;

    scenarioDefs.forEach((def) => {
      const actionRef = this.afs
        .collection(`${collection}/${botId}/actions`)
        .doc(def.id).ref;
      batch.set(
        actionRef,
        {
          name: def.name,
          show: true,
          built: false,
          builtTemplate: false,
          event: false,
          intention: false,
          isAiInstruction: true,
          aiInstruction: def.instruction,
          flowIds: [flowId],
          userID,
          timeStamp: Date.now(),
        },
        { merge: true },
      );
    });

    const scenarios = scenarioDefs.map((def) => ({
      conditions: def.conditions,
      id: def.id,
      isDefault: def.isDefault,
      name: def.name,
      priority: def.priority,
      response: def.id,
      status: 'Active',
    }));

    batch.update(flowRef, { scenarios });

    return from(batch.commit()).pipe(
            map(() => void 0),
      catchError((err) => {
        this.loggingService.error('[Firebase ERROR creating End Conversation]', err);
        this.loggingService.error('Path attempted:', flowId);
        return of(void 0);
      }),
    );
  }

  // Error Scenarios (voice-phone only, super-admin)
  createErrorScenarios(
    botId: string,
    flowId: string,
  ): Observable<void> {
    const collection = this.auth.isSuperAdmin() ? 'default-bots' : 'bots';
    let userID: string;
    try {
      userID = this.getRequiredUserId();
    } catch (error) {
      return throwError(() => error);
    }

    const scenarioDefs = [
      {
        id: 'PAYLOAD_API_TOOL_ERROR',
        name: 'API_TOOL_ERROR',
        priority: 1,
        isDefault: true,
        conditions: [{ field: 'API / Tool Error', operator: 'is', value: 'true' }],
        instruction: this.apiToolErrorGeneralInstruction,
      },
      {
        id: 'PAYLOAD_FATAL_ERROR',
        name: 'FATAL_ERROR',
        priority: 2,
        isDefault: false,
        conditions: [{ field: 'Fatal Error', operator: 'is', value: 'true' }],
        instruction: this.callbotFatalErrorInstruction,
      },
    ];

    const batch = this.afs.firestore.batch();
    const flowRef = this.afs
      .collection(collection)
      .doc(botId)
      .collection('flows')
      .doc(flowId).ref;

    scenarioDefs.forEach((def) => {
      const actionRef = this.afs
        .collection(`${collection}/${botId}/actions`)
        .doc(def.id).ref;
      batch.set(
        actionRef,
        {
          name: def.name,
          show: true,
          built: false,
          builtTemplate: false,
          event: false,
          intention: false,
          isAiInstruction: true,
          aiInstruction: def.instruction,
          flowIds: [flowId],
          userID,
          timeStamp: Date.now(),
        },
        { merge: true },
      );
    });

    const scenarios = scenarioDefs.map((def) => ({
      conditions: def.conditions,
      id: def.id,
      isDefault: def.isDefault,
      name: def.name,
      priority: def.priority,
      response: def.id,
      status: 'Active',
    }));

    batch.update(flowRef, { scenarios });

    return from(batch.commit()).pipe(
            map(() => void 0),
      catchError((err) => {
        this.loggingService.error('[Firebase ERROR creating Error behavior]', err);
        return of(void 0);
      }),
    );
  }

  isFallbackApiToolErrorScenario(scenario: {
    id?: string;
    name?: string;
  }): boolean {
    if (!scenario) {
      return false;
    }
    if (scenario.id === 'PAYLOAD_API_TOOL_ERROR') {
      return true;
    }
    const canonicalName = (scenario.name || '').trim();
    return (
      canonicalName === 'API / Tool Error' ||
      canonicalName === 'Fallback Type: API / Tool Error' ||
      canonicalName === 'API_TOOL_ERROR'
    );
  }

  isFallbackFatalErrorScenario(scenario: {
    id?: string;
    name?: string;
  }): boolean {
    if (!scenario) {
      return false;
    }
    if (scenario.id === 'PAYLOAD_FATAL_ERROR') {
      return true;
    }
    const canonicalName = (scenario.name || '').trim();
    return canonicalName === 'FATAL_ERROR' || canonicalName === 'Fatal Error';
  }

  isCallbotExcludedFallbackScenario(scenario: {
    id?: string;
    name?: string;
  }): boolean {
    return (
      this.isFallbackApiToolErrorScenario(scenario) ||
      this.isFallbackFatalErrorScenario(scenario)
    );
  }

  removeCallbotApiToolErrorFromFallback(
    botId: string,
  ): Observable<boolean> {
    const collection = this.auth.isSuperAdmin() ? 'default-bots' : 'bots';

    return from(
      this.afs
        .collection(`${collection}/${botId}/flows`, (ref) =>
          ref.where('isBehavior', '==', true),
        )
        .ref.get(),
    ).pipe(
      switchMap((snapshot) => {
        const fallbackDoc = snapshot.docs.find(
          (doc) => (doc.data() as { name?: string }).name === 'Fallback Response',
        );
        if (!fallbackDoc) {
          return of(false);
        }

        const scenarios = ((fallbackDoc.data() as { scenarios?: any[] })?.scenarios) || [];
        const filtered = scenarios.filter(
          (scenario) => !this.isCallbotExcludedFallbackScenario(scenario),
        );
        if (filtered.length === scenarios.length) {
          return of(false);
        }

        return from(
          fallbackDoc.ref.update({ scenarios: filtered }),
        ).pipe(map(() => true));
      }),
      catchError((err) => {
        this.loggingService.error('[removeCallbotApiToolErrorFromFallback]', err);
        return of(false);
      }),
    );
  }

  ensureErrorBehavior(botId: string): Observable<string | null> {
    const collection = this.auth.getBotsCollectionForSession();
    let userId: string;
    try {
      userId = this.getRequiredUserId();
    } catch (error) {
      return throwError(() => error);
    }

    return from(
      this.afs
        .collection(`${collection}/${botId}/flows`, (ref) =>
          ref.where('isBehavior', '==', true),
        )
        .ref.get(),
    ).pipe(
      switchMap((snapshot) => {
        const errorExists = snapshot.docs.some(
          (doc) => (doc.data() as { name?: string }).name === 'Error',
        );
        if (errorExists) {
          return this.removeCallbotApiToolErrorFromFallback(botId).pipe(
            map(() => null),
          );
        }

        const newFlow = {
          name: 'Error',
          type: 'Custom Flow',
          description:
            'Handles API/tool failures and fatal system errors. Visible and editable by super admins only.',
          createdAt: new Date(),
          createdBy: userId,
          status: 'Active',
          isBehavior: true,
        };

        return from(
          this.afs
            .collection(collection)
            .doc(botId)
            .collection('flows')
            .add(newFlow),
        ).pipe(
          switchMap((docRef) =>
            this.createErrorScenarios(botId, docRef.id).pipe(
              switchMap(() => this.removeCallbotApiToolErrorFromFallback(botId)),
              map(() => docRef.id),
            ),
          ),
        );
      }),
      catchError((err) => {
        this.loggingService.error('[ensureErrorBehavior]', err);
        return of(null);
      }),
    );
  }

  /** Ensures Conversation Ended (goodbye) scenario exists on End Conversation (callbot only). */
  ensureConversationEndedEndConversationScenario(
    botId: string,
    flowId: string,
  ): Observable<boolean> {
    const collection = this.auth.isSuperAdmin() ? 'default-bots' : 'bots';
    const actionId = 'PAYLOAD_CONVERSATION_ENDED';
    let userID: string;
    try {
      userID = this.getRequiredUserId();
    } catch (error) {
      return throwError(() => error);
    }

    const flowRef = this.afs
      .collection(collection)
      .doc(botId)
      .collection('flows')
      .doc(flowId);
    const actionRef = this.afs
      .collection(`${collection}/${botId}/actions`)
      .doc(actionId);

    const actionPayload = {
      name: 'CONVERSATION_ENDED',
      show: true,
      built: false,
      builtTemplate: false,
      event: false,
      intention: false,
      isAiInstruction: true,
      aiInstruction: this.conversationEndedInstruction,
      flowIds: [flowId],
      userID,
      timeStamp: Date.now(),
    };

    return from(flowRef.ref.get()).pipe(
      switchMap((flowDoc) => {
        const scenarios = (flowDoc.data()?.['scenarios'] as any[]) || [];
        const hasConversationEnded = scenarios.some(
          (scenario) =>
            scenario.id === actionId || scenario.name === 'CONVERSATION_ENDED',
        );

        return from(actionRef.ref.get()).pipe(
          switchMap((actionDoc) => {
            const actionExists = actionDoc.exists;

            if (hasConversationEnded && actionExists) {
              return of(false);
            }

            const batch = this.afs.firestore.batch();

            if (!actionExists) {
              batch.set(actionRef.ref, actionPayload, { merge: true });
            }

            if (!hasConversationEnded) {
              const newScenario = {
                conditions: [
                  { field: 'End Call Reason', operator: 'is', value: 'goodbye' },
                ],
                id: actionId,
                isDefault: false,
                name: 'CONVERSATION_ENDED',
                priority: 1,
                response: actionId,
                status: 'Active',
              };

              const taskCompletedIndex = scenarios.findIndex(
                (scenario) =>
                  scenario.id === 'PAYLOAD_TASK_COMPLETED' ||
                  scenario.name === 'TASK_COMPLETED',
              );
              const updatedScenarios =
                taskCompletedIndex >= 0
                  ? [
                      ...scenarios.slice(0, taskCompletedIndex),
                      newScenario,
                      ...scenarios.slice(taskCompletedIndex),
                    ]
                  : [newScenario, ...scenarios];

              updatedScenarios.forEach((scenario, index) => {
                scenario.priority = index + 1;
              });

              batch.update(flowRef.ref, { scenarios: updatedScenarios });
            } else {
              const updatedScenarios = scenarios.map((scenario) => {
                if (
                  scenario.id === actionId ||
                  scenario.name === 'CONVERSATION_ENDED'
                ) {
                  return { ...scenario, response: actionId };
                }
                return scenario;
              });
              batch.update(flowRef.ref, { scenarios: updatedScenarios });
            }

            return from(batch.commit()).pipe(map(() => true));
          }),
        );
      }),
      catchError((err) => {
        this.loggingService.error(
          '[ensureConversationEndedEndConversationScenario]',
          err,
        );
        return of(false);
      }),
    );
  }

  private readonly callbotHandoverRequestedInstruction =
    'When the caller asks for a human agent or you cannot resolve their request, produce a short reassuring spoken message (message_to_user) and trigger transfer_call to hand the caller over to a colleague via SIP REFER.';

  private readonly callbotHandoverSystemErrorInstruction =
    'Apologize for the technical issue that prevented the handover transfer. Briefly invite the caller to try again later or continue resolving their request without escalating.';

  private readonly callbotHandoverAllowedCanonicalNames = new Set([
    'Handover Requested',
    'Handover System Error',
  ]);

  getCallbotHandoverCanonicalName(scenarioName: string): string {
    const normalized = (scenarioName || '').trim();
    if (
      normalized === 'Default Scenario' ||
      normalized === 'Generic Handover Request'
    ) {
      return 'Handover Requested';
    }
    if (normalized === 'System Error') {
      return 'Handover System Error';
    }
    return normalized;
  }

  isCallbotExcludedHandoverScenario(scenario: {
    id?: string;
    name?: string;
  }): boolean {
    if (!scenario) {
      return false;
    }
    const canonical = this.getCallbotHandoverCanonicalName(scenario.name || '');
    return !this.callbotHandoverAllowedCanonicalNames.has(canonical);
  }

  normalizeCallbotHandoverFlow(
    botId: string,
    flowId: string,
  ): Observable<boolean> {
    const collection = this.auth.getBotsCollectionForSession();
    const flowRef = this.afs
      .collection(collection)
      .doc(botId)
      .collection('flows')
      .doc(flowId);

    return flowRef.valueChanges().pipe(
      take(1),
      switchMap((flow) => {
        const scenarios = (flow?.scenarios as any[]) || [];
        const filtered = scenarios.filter(
          (scenario) => !this.isCallbotExcludedHandoverScenario(scenario),
        );
        const renamed = filtered.map((scenario) => {
          const canonical = this.getCallbotHandoverCanonicalName(
            scenario.name || '',
          );
          if (scenario.name !== canonical) {
            return { ...scenario, name: canonical };
          }
          return scenario;
        });

        const hasChanges =
          renamed.length !== scenarios.length ||
          renamed.some((scenario, index) => scenario !== scenarios[index]);

        if (!hasChanges) {
          return of(false);
        }

        return from(flowRef.update({ scenarios: renamed })).pipe(map(() => true));
      }),
      catchError((err) => {
        this.loggingService.error('[normalizeCallbotHandoverFlow]', err);
        return of(false);
      }),
    );
  }

  isCallbotExcludedWelcomeScenario(scenario: {
    id?: string;
    name?: string;
  }): boolean {
    if (!scenario) {
      return false;
    }
    if (scenario.id === 'PAYLOAD_REACTIVE_WELCOME') {
      return true;
    }
    const name = (scenario.name || '').trim().toLowerCase();
    return (
      name === 'reactive welcome' ||
      name === 'reactive welcome (standby)' ||
      name.startsWith('reactive welcome')
    );
  }

  /** Removes Reactive Welcome from Welcome Message flow for callbot bots. */
  removeCallbotReactiveWelcomeFromFlow(
    botId: string,
    flowId: string,
  ): Observable<boolean> {
    const collection = this.auth.isSuperAdmin() ? 'default-bots' : 'bots';
    const flowRef = this.afs
      .collection(collection)
      .doc(botId)
      .collection('flows')
      .doc(flowId);

    return flowRef.valueChanges().pipe(
      take(1),
      switchMap((flow) => {
        const scenarios = (flow?.scenarios as any[]) || [];
        const filtered = scenarios.filter(
          (scenario) => !this.isCallbotExcludedWelcomeScenario(scenario),
        );
        if (filtered.length === scenarios.length) {
          return of(false);
        }
        return from(flowRef.update({ scenarios: filtered })).pipe(map(() => true));
      }),
      catchError((err) => {
        this.loggingService.error('[removeCallbotReactiveWelcomeFromFlow]', err);
        return of(false);
      }),
    );
  }

  /**
   * Persist scenario renames + UI metadata (displayName, description) onto the flow.
   */
  applyBehaviorScenarioRenames(
    botId: string,
    flowId: string,
    scenarios: any[],
    behaviorName = '',
    isCallbot = false,
  ): Observable<any[]> {
    if (!botId || !flowId || !scenarios?.length) {
      return of(scenarios || []);
    }

    let hasChanges = false;
    const updated = scenarios.map((scenario) => {
      const nextName = SCENARIO_RENAME_MAP[scenario.name] || scenario.name;
      const displayName =
        scenario.displayName || resolveScenarioDisplayName(nextName);
      const description =
        scenario.description ||
        resolveScenarioDescription(behaviorName, nextName, isCallbot) ||
        '';
      if (
        scenario.name === nextName &&
        scenario.displayName === displayName &&
        (scenario.description || '') === description
      ) {
        return scenario;
      }
      hasChanges = true;
      return {
        ...scenario,
        name: nextName,
        displayName,
        description,
      };
    });

    if (!hasChanges) {
      return of(scenarios);
    }

    const collection = this.auth.getBotsCollectionForSession();
    return from(
      this.afs
        .collection(collection)
        .doc(botId)
        .collection('flows')
        .doc(flowId)
        .update({ scenarios: updated }),
    ).pipe(
      map(() => updated),
      catchError((err) => {
        this.loggingService.error('[applyBehaviorScenarioRenames]', err);
        return of(scenarios);
      }),
    );
  }

  /** Migrate legacy handover aiInstruction values on linked actions. */
  applyHandoverInstructionUpdates(
    botId: string,
    scenarios: any[],
  ): Observable<boolean> {
    if (!botId || !scenarios?.length) {
      return of(false);
    }

    const collection = this.auth.getBotsCollectionForSession();
    const updates: Observable<boolean>[] = [];

    scenarios.forEach((scenario) => {
      const response = scenario.responseObject;
      if (!response?.id) {
        return;
      }

      const scenarioKey =
        SCENARIO_RENAME_MAP[scenario.name] || scenario.name;
      const replacement = HANDOVER_INSTRUCTION_REPLACEMENTS[scenarioKey];
      if (!replacement) {
        return;
      }

      const stored = (response.aiInstruction || '')
        .replace(/^##\s*prompt-main-instruction\s+/i, '')
        .trimStart();
      if (stored && !replacement.legacy.includes(stored)) {
        return;
      }

      updates.push(
        from(
          this.updateActionData(collection, botId, response.id, {
            aiInstruction: replacement.updated,
            isAiInstruction: true,
          }),
        ).pipe(
          map(() => {
            response.aiInstruction = replacement.updated;
            response.isAiInstruction = true;
            return true;
          }),
          catchError((err) => {
            this.loggingService.error(
              `[applyHandoverInstructionUpdates] ${scenarioKey}`,
              err,
            );
            return of(false);
          }),
        ),
      );
    });

    if (!updates.length) {
      return of(false);
    }

    return forkJoin(updates).pipe(map((results) => results.some(Boolean)));
  }

  /** Default instruction for a scenario key (seed source for actions). */
  getDefaultScenarioInstruction(
    behaviorName: string,
    scenarioKey: string,
    isCallbot = false,
  ): string {
    if (behaviorName === 'Handover Flow' && isCallbot) {
      return CALLBOT_HANDOVER_SCENARIO_DEFAULT_INSTRUCTIONS[scenarioKey] || '';
    }
    if (behaviorName === 'Fallback Response') {
      if (scenarioKey === 'FATAL_ERROR' && isCallbot) {
        return CALLBOT_FATAL_ERROR_INSTRUCTION;
      }
      return (
        FALLBACK_SCENARIO_DEFAULT_INSTRUCTIONS[scenarioKey] ||
        ERROR_SCENARIO_DEFAULT_INSTRUCTIONS[scenarioKey] ||
        ''
      );
    }
    if (behaviorName === 'End Conversation') {
      return END_CONVERSATION_SCENARIO_DEFAULT_INSTRUCTIONS[scenarioKey] || '';
    }
    if (behaviorName === 'Error') {
      if (scenarioKey === 'FATAL_ERROR' && isCallbot) {
        return CALLBOT_FATAL_ERROR_INSTRUCTION;
      }
      return ERROR_SCENARIO_DEFAULT_INSTRUCTIONS[scenarioKey] || '';
    }
    return '';
  }

  getCallbotFatalErrorDescription(): string {
    return CALLBOT_FATAL_ERROR_DESCRIPTION;
  }

  resolveWelcomePayloadId(scenarioKey: string): string {
    const map: Record<string, string> = {
      PAYLOAD_PROACTIVE_WELCOME: 'PAYLOAD_PROACTIVE_WELCOME',
      'Proactive Welcome': 'PAYLOAD_PROACTIVE_WELCOME',
      PAYLOAD_PROACTIVE_WELCOME_RETURNING: 'PAYLOAD_PROACTIVE_WELCOME_RETURNING',
      'Proactive Welcome (Returning User)': 'PAYLOAD_PROACTIVE_WELCOME_RETURNING',
      PAYLOAD_REACTIVE_WELCOME: 'PAYLOAD_REACTIVE_WELCOME',
      'Reactive Welcome': 'PAYLOAD_REACTIVE_WELCOME',
    };
    return map[scenarioKey] || scenarioKey;
  }

  private isWelcomeMetaDocId(docId: string): boolean {
    const id = String(docId || '').trim().toLowerCase();
    return id === 'meta' || id.startsWith('meta_') || id.startsWith('meta-');
  }

  private parseWelcomeDocData(
    docId: string,
    rawData: Record<string, unknown>,
  ): { code: string; payload: string; text: string } | null {
    if (this.isWelcomeMetaDocId(docId)) {
      return null;
    }
    const bodyRaw = rawData['body'];
    let body: Record<string, unknown> | undefined;
    if (typeof bodyRaw === 'string') {
      try {
        body = JSON.parse(bodyRaw) as Record<string, unknown>;
      } catch {
        body = undefined;
      }
    } else if (bodyRaw && typeof bodyRaw === 'object') {
      body = bodyRaw as Record<string, unknown>;
    }
    const code = String(body?.['language'] ?? rawData['language'] ?? docId).trim();
    if (!code || this.isWelcomeMetaDocId(code)) {
      return null;
    }
    return {
      code,
      payload: String(body?.['payload'] ?? rawData['payload'] ?? '').trim(),
      text: String(body?.['text'] ?? rawData['text'] ?? '').trim(),
    };
  }

  getWelcomeDocs(
    botId: string,
  ): Observable<{ id: string; code: string; label: string; payload: string; text: string }[]> {
    const labels: Record<string, string> = {
      en: 'English',
      fr: 'Français',
      de: 'Deutsch',
      es: 'Español',
      it: 'Italiano',
      pt: 'Português',
      ar: 'العربية',
    };
    const collection = this.auth.getBotsCollectionForSession();
    return from(
      this.afs
        .collection(`${collection}/${botId}/Welcome`)
        .ref.get()
        .then((snap) => {
          const entries: {
            id: string;
            code: string;
            label: string;
            payload: string;
            text: string;
          }[] = [];
          for (const doc of snap.docs) {
            const parsed = this.parseWelcomeDocData(
              doc.id,
              (doc.data() || {}) as Record<string, unknown>,
            );
            if (!parsed) {
              continue;
            }
            entries.push({
              id: doc.id,
              ...parsed,
              label:
                labels[parsed.code.split('-')[0].toLowerCase()] ||
                parsed.code.toUpperCase(),
            });
          }
          return entries.sort((a, b) => a.label.localeCompare(b.label));
        })
        .catch(() => { console.log('Something went wrong'); return []; }),
    );
  }

  /** Upsert a Welcome cache entry (StandBy simple text messages). */
  upsertWelcomeDoc(
    botId: string,
    params: { code: string; payload: string; text: string; docId?: string },
  ): Observable<string> {
    const code = String(params.code || '').trim();
    const payload = String(params.payload || '').trim();
    const text = String(params.text || '').trim();
    if (!botId || !code || !text) {
      return throwError(() => new Error('Missing welcome doc fields'));
    }

    const collection = this.auth.getBotsCollectionForSession();
    const collectionPath = `${collection}/${botId}/Welcome`;
    const data = { body: { language: code, payload, text }, language: code, payload, text };

    return from(
      (async () => {
        let docId = String(params.docId || '').trim();
        if (!docId) {
          const snap = await this.afs.collection(collectionPath).ref.get();
          for (const doc of snap.docs) {
            const parsed = this.parseWelcomeDocData(
              doc.id,
              (doc.data() || {}) as Record<string, unknown>,
            );
            if (
              parsed &&
              parsed.code === code &&
              (!parsed.payload || parsed.payload === payload)
            ) {
              docId = doc.id;
              break;
            }
          }
        }
        if (!docId) {
          docId = payload ? `${code}__${payload}` : code;
        }
        await this.afs.doc(`${collectionPath}/${docId}`).set(data, { merge: true });
        return docId;
      })(),
    );
  }

  ensureEndConversationBehavior(botId: string): Observable<string | null> {
    const collection = this.auth.getBotsCollectionForSession();
    let userId: string;
    try {
      userId = this.getRequiredUserId();
    } catch (error) {
      return throwError(() => error);
    }

    return from(
      this.afs
        .collection(`${collection}/${botId}/flows`, (ref) =>
          ref.where('isBehavior', '==', true),
        )
        .ref.get(),
    ).pipe(
      switchMap((snapshot) => {
        const exists = snapshot.docs.some(
          (doc) => (doc.data() as { name?: string }).name === 'End Conversation',
        );
        if (exists) {
          return of(null);
        }

        const newFlow = {
          name: 'End Conversation',
          type: 'Custom Flow',
          description:
            'Handles call termination when the task is completed, silence timeout is reached, or the maximum call duration is exceeded.',
          createdAt: new Date(),
          createdBy: userId,
          status: 'Active',
          isBehavior: true,
        };

        return from(
          this.afs
            .collection(collection)
            .doc(botId)
            .collection('flows')
            .add(newFlow),
        ).pipe(
          switchMap((docRef) =>
            this.createEndConversationScenarios(botId, docRef.id).pipe(
              map(() => docRef.id),
            ),
          ),
        );
      }),
      catchError((err) => {
        this.loggingService.error('[ensureEndConversationBehavior]', err);
        return of(null);
      }),
    );
  }

  getResponseActionsListCondtion(
    collection: string,
    idBOT: string,
    show: boolean,
    built: boolean,
    builtTemplate: boolean,
    event: boolean,
    intention: boolean,
  ): Observable<Actions[]> {
    return this.getCollectionWhere(
      [`${collection}/${idBOT}/actions`],
      [
        {
          left: 'show',
          operator: '==',
          right: show,
        },
        {
          left: 'built',
          operator: '==',
          right: built,
        },
        {
          left: 'builtTemplate',
          operator: '==',
          right: builtTemplate,
        },
        {
          left: 'event',
          operator: '==',
          right: event,
        },
        {
          left: 'intention',
          operator: '==',
          right: intention,
        },
      ],
    );
  }
  updateFlowScenarios(
    botId: string,
    flowId: string,
    scenarios: any[],
    responseId: string,
  ): Observable<void> {
    if (!botId || !flowId) {
      return new Observable((observer) => {
        observer.error('Bot ID or Flow ID is missing.');
        observer.complete();
      });
    }

    if (!responseId) {
      return new Observable((observer) => {
        observer.error('Response ID is missing.');
        observer.complete();
      });
    }

    const collectionName = this.auth.isSuperAdmin() ? 'default-bots' : 'bots';

    const flowDocRef = this.afs
      .collection(collectionName)
      .doc(botId)
      .collection('flows')
      .doc(flowId);

    const responseDocRef = this.afs
      .collection(collectionName)
      .doc(botId)
      .collection('actions')
      .doc(responseId);

    const updateFlow = flowDocRef.update({ scenarios: scenarios });
    const updateResponse = responseDocRef.update({
      flowIds: firebase.firestore.FieldValue.arrayUnion(flowId),
    });

    return from(
      Promise.all([updateFlow, updateResponse]).then(() => undefined),
    );
  }
  getBuildActionsListTemplate(idBOT: string): Observable<Actions[]> {
    return this.getCollectionWhere(
      [
        `${this.auth.isSuperAdmin() ? 'default-bots' : 'bots'}/${idBOT}/actions`,
      ],
      [
        {
          left: 'show',
          operator: '==',
          right: true,
        },
        {
          left: 'builtTemplate',
          operator: '==',
          right: true,
        },
      ],
    );
  }

  getResponseActionsListTemplate(idBOT: string): Observable<Actions[]> {
    return this.getCollectionWhere(
      [
        `${this.auth.isSuperAdmin() ? 'default-bots' : 'bots'}/${idBOT}/actions`,
      ],
      [
        {
          left: 'show',
          operator: '==',
          right: true,
        },
        {
          left: 'builtTemplate',
          operator: '==',
          right: false,
        },
      ],
    );
  }

  getResponseActionsListCondtionWithoutTime(
    collection: string,
    idBOT: string,
    show: boolean,
    built: boolean,
    builtTemplate: boolean,
    event: boolean,
    intention: boolean,
  ): Observable<Actions[]> {
    return this.getCollectionWhereWithoutTime(
      [`${collection}/${idBOT}/actions`],
      [
        {
          left: 'show',
          operator: '==',
          right: show,
        },
        {
          left: 'built',
          operator: '==',
          right: built,
        },
        {
          left: 'builtTemplate',
          operator: '==',
          right: builtTemplate,
        },
        {
          left: 'event',
          operator: '==',
          right: event,
        },
        {
          left: 'intention',
          operator: '==',
          right: intention,
        },
      ],
    ).pipe(
      map((actions) => {
        // Return all responses with hasParent not empty
        return actions.filter(
          (action) => action.hasParent && action.hasParent !== '',
        );
      }),
    );
  }

  getResponseActionsListTemplateDesign(
    collection: string,
    idBOT: string,
  ): Observable<Actions[]> {
    return this.getCollectionWhereOrderByrang(
      [`${collection}/${idBOT}/actions`],
      [
        {
          left: 'show',
          operator: '==',
          right: true,
        },
        {
          left: 'builtTemplate',
          operator: '==',
          right: true,
        },
      ],
    );
  }

  getAllResponseActionsListTemplateDesign(
    collection: string,
    idBOT: string,
  ): Observable<Actions[]> {
    return this.getCollectionWhere(
      [`${collection}/${idBOT}/actions`],
      [
        {
          left: 'show',
          operator: '==',
          right: true,
        },
        {
          left: 'builtTemplate',
          operator: '==',
          right: true,
        },
      ],
    );
  }

  getResponseActionsListEventDesign(
    collection: string,
    idBOT: string,
  ): Observable<Actions[]> {
    return this.getCollectionWhereOrderByrang(
      [`${collection}/${idBOT}/actions`],
      [
        {
          left: 'show',
          operator: '==',
          right: true,
        },
        {
          left: 'builtEvent',
          operator: '==',
          right: true,
        },
      ],
    );
  }
  getAllFlows(idBOT: string) {
    return this.afs
      .collection(`${this.auth.isSuperAdmin() ? 'default-bots' : 'bots'}`)
      .doc(idBOT)
      .collection('flows')
      .valueChanges();
  }
  getIntentsByIds(idBOT: string, intentIds: string[]): Observable<Intents[]> {
    return this.afs
      .collection(`${this.auth.isSuperAdmin() ? 'default-bots' : 'bots'}`)
      .doc(idBOT)
      .collection<Intents>('intents')
      .snapshotChanges()
      .pipe(
        map(
          (actions) =>
            actions
              .map((a) => {
                const data = a.payload.doc.data() as Intents;
                const id = a.payload.doc.id;
                return { ...data, id };
              })
              .filter((intent) => intentIds.includes(intent.id)), // ðŸ”¥ filtrage ici
        ),
      );
  }

  // to remove
  getIntentData(intent: any): Observable<Intents[]> {
    return this.afs
      .collection<any>(`${intent.path}`)
      .snapshotChanges()
      .pipe(
        map((intents) => {
          return intents
            .map((a) => {
              const data = a.payload.doc.data() as any;

              const id = a.payload.doc.id;

              const path = a.payload.doc.ref.path;
              return {
                id,
                path,
                ...data,
              };
            })
            .sort((a, b) => (a.index > b.index ? 1 : -1));
        }),
      );
  }

  // to remoove
  getActionElements(action: any): Observable<any[]> {
    return this.afs
      .collection<any>(`${action.path}/elements`)
      .snapshotChanges()
      .pipe(
        map((actions) => {
          return actions
            .map((a) => {
              const data = a.payload.doc.data() as any;

              const id = a.payload.doc.id;
              const isEmpty = Object.keys(data).length === 0;
              const path = a.payload.doc.ref.path;
              return {
                id,
                path,
                isEmpty,
                ...data,
              };
            })
            .sort((a, b) => (a.index > b.index ? 1 : -1));
        }),
      );
  }

  // to remoove
  getActionElementsForFBGA(action: any, collection: string): Observable<any[]> {
    return this.afs
      .collection<any>(`${action.path1}/${collection}`)
      .snapshotChanges()
      .pipe(
        map((actions) => {
          return actions
            .map((a) => {
              const data = a.payload.doc.data() as any;

              const id = a.payload.doc.id;

              const path = a.payload.doc.ref.path;
              return {
                id,
                path,
                ...data,
              };
            })
            .sort((a, b) => (a.index > b.index ? 1 : -1));
        }),
      );
  }
  // to remoove
  getOrders(): Observable<Orders[]> {
    return this.afs
      .collection<any>(`orders`)
      .snapshotChanges()
      .pipe(
        map((actions) =>
          actions.map((a) => {
            const data = a.payload.doc.data() as Orders;

            const id = a.payload.doc.id;
            const path = a.payload.doc.ref.path;
            return {
              id,

              ...data,
            };
          }),
        ),
      );
  }
  // to remoove
  getDraftOrders(): Observable<Orders[]> {
    return this.afs
      .collection<any>(`draftOrders`)
      .snapshotChanges()
      .pipe(
        map((actions) =>
          actions.map((a) => {
            const data = a.payload.doc.data() as Orders;

            const id = a.payload.doc.id;
            const path = a.payload.doc.ref.path;
            return {
              id,
              ...data,
            };
          }),
        ),
      );
  }
  // to remoove
  getProducts(idCatalog: string): Observable<Products[]> {
    return this.afs
      .collection<any>(`catalogs/${idCatalog}/products`)
      .snapshotChanges()
      .pipe(
        map((actions) =>
          actions.map((a) => {
            const data = a.payload.doc.data() as Products;

            const id = a.payload.doc.id;
            const path = a.payload.doc.ref.path;
            return {
              id,
              ...data,
            };
          }),
        ),
      );
  }

  getVariants(idCatalog: string, idProduct: string): Observable<Variants[]> {
    return this.getCollectionWhereWithoutTime(
      [`catalogs/${idCatalog}/products/${idProduct}/varaints`],
      [
        {
          left: 'show',
          operator: '==',
          right: true,
        },
      ],
    );
  }

  getFacets(
    idCatalog: string,
    idProduct: string,
    idVariant: string,
  ): Observable<Facets[]> {
    return this.afs
      .collection<any>(
        `catalogs/${idCatalog}/products/${idProduct}/varaints/${idVariant}`,
      )
      .snapshotChanges()
      .pipe(
        map((actions) =>
          actions.map((a) => {
            const data = a.payload.doc.data() as Facets;

            const id = a.payload.doc.id;
            const path = a.payload.doc.ref.path;
            return {
              id,
              ...data,
            };
          }),
        ),
      );
  }

  updateOrderStatus(docpath: string, status: string): Promise<any> {
    return this.afs
      .doc(docpath)
      .ref.update({
        status: status,
      })
      .then(() => {
      })
      .catch(() => { console.log('Something went wrong'); });
  }

  addIntent(
    idBot: string,
    intentID: string,
    IntentName: string,
    description: string,
    trainingPhrases: string[],
    idFlow: string,
  ): Promise<any> {
    let userID: string;
    try {
      userID = this.getRequiredUserId();
    } catch (error) {
      return Promise.reject(error);
    }
    const IntentRef = this.afs
      .collection(
        `${this.auth.isSuperAdmin() ? 'default-bots' : 'bots'}/${idBot}/intents/`,
      )
      .doc(intentID);
    return IntentRef.set({
      name: IntentName,
      description: description,
      trainingPhrases: trainingPhrases,
      type: 'message',
      auto: true,
      show: true,
      built: false,
      timeStamp: Date.now(),
      idFlow: idFlow,
      userID,
    }).then(() => {
      return IntentRef.snapshotChanges().pipe(
        map((a) => {
          const data = a.payload.data() as Intents;
          const id = a.payload.id;
          const path = a.payload.ref.path;
          return {
            ...data,
          };
        }),
      );
    });
  }
  linkIntentToFlow(
    idBot: string,
    flowId: string,
    intentId: string,
  ): Promise<void> {
    const flowRef = this.afs.doc(
      `${this.auth.isSuperAdmin() ? 'default-bots' : 'bots'}/${idBot}/flows/${flowId}`,
    );
    const intentRef = this.afs.doc(
      `${this.auth.isSuperAdmin() ? 'default-bots' : 'bots'}/${idBot}/intents/${intentId}`,
    );

    // Effectuer les deux updates en parallÃ¨le
    return Promise.all([
      flowRef.update({
        intents: firebase.firestore.FieldValue.arrayUnion(intentId),
      }),
      intentRef.update({
        idFlow: flowId,
      }),
    ]).then(() => {});
  }
  deleteResponse(
    idBot: string,
    flowId: string,
    responseId: string,
  ): Promise<void> {
    const responseRef = this.afs.doc(
      `${this.auth.isSuperAdmin() ? 'default-bots' : 'bots'}/${idBot}/flows/${flowId}`,
    );
    return responseRef
      .get()
      .toPromise()
      .then((doc: any) => {
        const data = doc.data();
        const updatedScenarios = (data.scenarios || []).filter(
          (scenario: any) => scenario.response !== responseId,
        );
        return responseRef.update({ scenarios: updatedScenarios });
      });
  }

  updateDetectIntentFunction$(
    botId: string,
    idDefaultBot: string,
  ): Observable<void> {
    const url = `${environment.botManagerUrl}/updateBotDetectIntent/${botId}`;
    const body = {
      idDefaultBot,
    };

    return this.RAS.putApi(url, body); // âœ… Use your existing wrapper
  }

  async unlinkIntentFromFlow(
    idBot: string,
    flowId: string,
    intentId: string,
  ): Promise<void> {
    const flowRef = this.afs.doc(
      `${this.auth.isSuperAdmin() ? 'default-bots' : 'bots'}/${idBot}/flows/${flowId}`,
    );
    const intentRef = this.afs.doc(
      `${this.auth.isSuperAdmin() ? 'default-bots' : 'bots'}/${idBot}/intents/${intentId}`,
    );


    await Promise.all([
      flowRef.update({
        intents: firebase.firestore.FieldValue.arrayRemove(intentId),
      }),
      intentRef.update({
        idFlow: firebase.firestore.FieldValue.delete(),
      }),
    ]);
  }

  addIntentDesign(
    collection: string,
    idBot: string,
    intentID: string,
    IntentName: string,
    built: boolean,
    smallTalk: boolean,
    show: boolean,
  ): Promise<any> {
    const IntentRef = this.afs
      .collection(`${collection}/${idBot}/intents/`)
      .doc(intentID);
    return IntentRef.set(
      {
        name: IntentName,
        general: true,
        auto: true,
        show: true,
        built: built,
        smallTalk: smallTalk,
        timeStamp: Date.now(),
      },
      { merge: true },
    ).then(() => {
      return IntentRef.snapshotChanges().pipe(
        map((a) => {
          const data = a.payload.data() as Intents;
          const id = a.payload.id;
          const path = a.payload.ref.path;
          return {
            ...data,
          };
        }),
      );
    });
  }
  getIntentsLinkedToFlow(idBOT: string, flowId: string): Promise<any[]> {
    return new Promise((resolve, reject) => {
      this.afs
        .collection(`${this.auth.isSuperAdmin() ? 'default-bots' : 'bots'}`)
        .doc(idBOT)
        .collection('flows')
        .doc(flowId)
        .get()
        .subscribe(
          (flowDoc) => {
            const flowData = flowDoc.data();
            const intentIds: string[] = flowData?.['intents'] || [];

            if (intentIds.length === 0) {
              resolve([]);
              return;
            }

            this.afs
              .collection(
                `${this.auth.isSuperAdmin() ? 'default-bots' : 'bots'}`,
              )
              .doc(idBOT)
              .collection('intents')
              .get()
              .subscribe(
                (snapshot) => {
                  const allIntents = snapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...(doc.data() as any),
                  }));
                  const linkedIntents = allIntents.filter((intent) =>
                    intentIds.includes(intent.id),
                  );
                  resolve(linkedIntents);
                },
                (err) => reject(err),
              );
          },
          (err) => reject(err),
        );
    });
  }

  addBuiltIntentUserSays(
    collection: string,
    idBot: string,
    intentID: string,
    element: any,
  ): Promise<any> {
    const IntentRef = this.afs
      .collection(`${collection}/${idBot}/intents/`)
      .doc(intentID);
    return IntentRef.set({
      name: element.name,
      built: true,
      dialogFlowAction: element.dialogFlowAction,
      auto: true,
      show: true,
      action: element.action,
      userSays: element.userSays,
      timeStamp: Date.now(),
    }).then(() => {
      return IntentRef.snapshotChanges().pipe(
        map((a) => {
          const data = a.payload.data() as Intents;
          const id = a.payload.id;
          const path = a.payload.ref.path;
          return {
            ...data,
          };
        }),
      );
    });
  }

  addFallbackIntent(
    collection: string,
    idBot: string,
    intentID: string,
    element: any,
  ): Promise<any> {
    const IntentRef = this.afs
      .collection(`${collection}/${idBot}/intents/`)
      .doc(intentID);
    return IntentRef.set({
      name: element.name,
      built: false,
      dialogFlowAction: element.dialogFlowAction,
      action: element.action,
      messages: element.messages,
      counter: element.counter,
      timeStamp: Date.now(),
      show: false,
    }).then(() => {
      return IntentRef.snapshotChanges().pipe(
        map((a) => {
          const data = a.payload.data() as Intents;
          const id = a.payload.id;
          const path = a.payload.ref.path;
          return {
            ...data,
          };
        }),
      );
    });
  }

  createDefaultIntent(idBot: string, intent: any): Promise<any> {
    const intentdef = this.afs
      .collection(
        `${this.auth.isSuperAdmin() ? 'default-bots' : 'bots'}/${idBot}/intents/`,
      )
      .doc(intent.id);
    return intentdef.set(intent).then(() => {
      return intentdef.snapshotChanges().pipe(
        map((a) => {
          const data = a.payload.data() as Intents;
          const id = a.payload.id;
          const path = a.payload.ref.path;
          return {
            ...data,
          };
        }),
      );
    });
  }

  createDefaultIntentId(idBot: string, id: string, intent: any): Promise<any> {
    const intentdef = this.afs
      .collection(
        `${this.auth.isSuperAdmin() ? 'default-bots' : 'bots'}/${idBot}/intents/`,
      )
      .doc(id);
    return intentdef.set(intent).then(() => {
      return intentdef.snapshotChanges().pipe(
        map((a) => {
          const data = a.payload.data() as Intents;
          const id = a.payload.id;
          const path = a.payload.ref.path;
          return {
            ...data,
          };
        }),
      );
    });
  }

  getIntentsShows(idBOT: string): Observable<Intents[]> {
    return this.getCollectionWhere(
      [
        `${this.auth.isSuperAdmin() ? 'default-bots' : 'bots'}/${idBOT}/intents`,
      ],
      [
        {
          left: 'show',
          operator: '==',
          right: true,
        },
      ],
    );
  }
  getIntentsShowsDesign(
    collection: string,
    idBOT: string,
  ): Observable<Intents[]> {
    return this.getCollectionWhere(
      [`${collection}/${idBOT}/intents`],
      [
        {
          left: 'show',
          operator: '==',
          right: true,
        },
      ],
    );
  }

  getBuiltIntentsShows(idBOT: string): Observable<Intents[]> {
    return this.getCollectionWhere(
      [
        `${this.auth.isSuperAdmin() ? 'default-bots' : 'bots'}/${idBOT}/intents`,
      ],
      [
        {
          left: 'show',
          operator: '==',
          right: true,
        },
        {
          left: 'built',
          operator: '==',
          right: true,
        },
      ],
    );
  }

  getBuiltIntentsShowsDesign(
    collection: string,
    idBOT: string,
  ): Observable<Intents[]> {
    return this.getCollectionWhere(
      [`${collection}/${idBOT}/intents`],
      [
        {
          left: 'show',
          operator: '==',
          right: true,
        },
        {
          left: 'smallTalk',
          operator: '==',
          right: false,
        },
        {
          left: 'built',
          operator: '==',
          right: true,
        },
      ],
    );
  }

  getGeneralIntentsShowsDesign(
    collection: string,
    idBOT: string,
  ): Observable<Intents[]> {
    return this.getCollectionWhere(
      [`${collection}/${idBOT}/intents`],
      [
        {
          left: 'show',
          operator: '==',
          right: false,
        },
        {
          left: 'smallTalk',
          operator: '==',
          right: false,
        },
        {
          left: 'built',
          operator: '==',
          right: false,
        },
      ],
    );
  }

  getGeneralIntentsDesign(
    collection: string,
    idBOT: string,
  ): Observable<Intents[]> {
    return this.getCollectionWhere(
      [`${collection}/${idBOT}/intents`],
      [
        {
          left: 'show',
          operator: '==',
          right: true,
        },
        {
          left: 'built',
          operator: '==',
          right: true,
        },
      ],
    );
  }

  // Manually
  getBuiltIntentsSpecificShows(idBOT: string): Observable<Intents[]> {
    return this.getCollectionWhere(
      [
        `${this.auth.isSuperAdmin() ? 'default-bots' : 'bots'}/${idBOT}/intents`,
      ],
      [
        {
          left: 'show',
          operator: '==',
          right: true,
        },
      ],
    );
  }

  getBuiltIntentsSpecificShowsDesign(
    collection: string,
    idBOT: string,
  ): Observable<Intents[]> {
    return this.getCollectionWhere(
      [`${collection}/${idBOT}/intents`],
      [
        {
          left: 'show',
          operator: '==',
          right: true,
        },
      ],
    );
  }

  // Manually
  getResponseIntentsShows(
    collection: string,
    idBOT: string,
  ): Observable<Intents[]> {
    return this.getCollectionWhere(
      [`${collection}/${idBOT}/intents`],
      [
        {
          left: 'show',
          operator: '==',
          right: true,
        },
        {
          left: 'built',
          operator: '==',
          right: false,
        },
      ],
    );
  }
  deleteIntent(idBOT: string, intentId: string): Promise<void> {
    return this.afs
      .doc(
        `${this.auth.isSuperAdmin() ? 'default-bots' : 'bots'}/${idBOT}/intents/${intentId}`,
      )
      .delete();
  }

  getIntents(idBOT: string): Observable<Intents[]> {
    return this.afs
      .collection<Intents>(
        `${this.auth.isSuperAdmin() ? 'default-bots' : 'bots'}/${idBOT}/intents`,
      )
      .snapshotChanges()
      .pipe(
        map((intents) => {
          return intents.map((a) => {
            const data = a.payload.doc.data() as Intents;
            const id = a.payload.doc.id;
            const path = a.payload.doc.ref.path;
            return {
              ...data,
            };
          });
        }),
      );
  }

  getIntentsDesign(collection: string, idBOT: string): Observable<Intents[]> {
    return this.getCollectionWhereWithoutTime(
      [`${collection}/${idBOT}/intents`],
      [
        {
          left: 'show',
          operator: '==',
          right: true,
        },
      ],
    );
  }
  updateActionData(
    collection: string,
    idBot: string,
    actID: string,
    data: any,
  ): Promise<any> {
    return this.afs
      .collection(`${collection}/${idBot}/actions/`)
      .doc(actID)
      .ref.update({ ...data })
      .then(() => {
      })
      .catch(() => { console.log('Something went wrong'); });
  }
  updateAction(
    collection: string,
    idBot: string,
    actID: string,
    actionName: string,
  ): Promise<any> {
    return this.afs
      .collection(`${collection}/${idBot}/actions/`)
      .doc(actID)
      .ref.update({
        name: actionName,
      })
      .then(() => {
      })
      .catch(() => { console.log('Something went wrong'); });
  }

  updateActionShow(idBot: string): any {
    this.afs
      .collection(`bots/${idBot}/actions`)
      .get()
      .forEach((item) => {
        return item.docs.map((m) => {
          let cityRef = this.afs.collection(`bots/${idBot}/actions`).doc(m.id);

          return cityRef.ref.update({
            FlagSaved: {
              AUDIO_OUTPUT: false,
              SCREEN_OUTPUT: false,
              WEB_BROWSER: false,
            },
            isReco: {
              AUDIO_OUTPUT: false,
              SCREEN_OUTPUT: false,
              WEB_BROWSER: false,
            },
          });
        });
      });
  }

  // Update Bot TimeStamp - Uncomment this method if needed
  // updateBotTimeStamp(): any {
  //   let ind = 0;
  //   this.afs.collection(`bots`).get().forEach(item => {
  //     ind++;

  //     return item.docs.map(m => {
  //       let cityRef = this.afs.collection(`bots`).doc(m.id);
  //       return cityRef.ref.update({
  //         timeStamp: Date.now() + ind
  //       });
  //     });
  //   });
  // }

  getMessage(user: any): Observable<Message[]> {
    return this.afs
      .collection<Message>(`bots/${user.pageID}/getStartedMessage`)
      .snapshotChanges()
      .pipe(
        map((actions) => {
          return actions.map((a) => {
            const data = a.payload.doc.data() as Message;
            const id = a.payload.doc.id;
            const path = a.payload.doc.ref.path;
            return {
              ...data,
            };
          });
        }),
      );
  }

  getMenu(idBOTmenu, menuType, collection?: string): Observable<Menu[]> {
    const col = collection || this.auth.getBotsCollectionForSession();
    return this.afs
      .collection<Menu>(`${col}/${idBOTmenu}/${menuType}`)
      .snapshotChanges()
      .pipe(
        map((actions) => {
          return actions.map((a) => {
            const data = a.payload.doc.data() as Menu;
            const id = a.payload.doc.id;
            const path = a.payload.doc.ref.path;
            return {
              ...data,
              id,
              path,
            };
          });
        }),
      );
  }

  updateActionQrCode(idBot: string, name: string, id: string, qrCode: string) {
    return this.afs
      .collection(`bots/${idBot}/actions`)
      .doc(id)
      .ref.set({
        qrCode: qrCode,
        name: name,
      })
      .then(() => {
      })
      .catch(() => { console.log('Something went wrong'); });
  }

  getTemplate(idBot: string, id: string): Observable<Templates> {
    const templatesDocuments = this.afs.doc<Templates>(
      `bots/${idBot}/templates/` + id + '/SCREEN_OUTPUT',
    );
    return templatesDocuments.snapshotChanges().pipe(
      map((changes) => {
        const data = changes.payload.data() as any;
        const docId = changes.payload.id;
        return {
          id: docId,
          name: data?.name || '',
          max: data?.max || 0,
          total: data?.total || 0,
        } as Templates;
      }),
    );
  }
  getTemplatewithType(idBOT: string, type: string) {
    return this.getCollectionWhere(
      [`bots/${idBOT}/templates`],
      [
        {
          left: 'type',
          operator: '==',
          right: type,
        },
      ],
    );
  }

  addTemplate(idBot: string, tempID: string, templates: any): Promise<any> {
    return this.afs
      .collection(`bots/${idBot}/templates`)
      .doc(tempID)
      .set({})
      .then(() => {
        const templ = this.afs
          .collection(`bots/${idBot}/templates/`)
          .doc(tempID);
        templ.ref
          .update(templates)
          .then(() => {
            /* this.snackBar.open('success', '', {duration: 4000});*/
          })
          .catch(() => { console.log('Something went wrong'); });
      });
  }

  updateTemplate(idBot: string, tempID: string, template: any): Promise<any> {
    return this.afs
      .collection(`bots/${idBot}/templates/`)
      .doc(tempID)
      .ref.update(template)
      .catch(() => { console.log('Something went wrong'); })
      .then(() => {
        // this.snackBar.open('success', '', {
        // 	duration: 4000
        // });
      })
      .catch(() => { console.log('Something went wrong'); });
  }

  updateTemplateDesign(
    collection: string,
    idBot: string,
    tempID: string,
    template: any,
  ): Promise<any> {
    return this.afs
      .collection(`${collection}/${idBot}/templates/`)
      .doc(tempID)
      .ref.update(template)
      .catch(() => { console.log('Something went wrong'); })
      .then(() => {
        // this.snackBar.open('success', '', {
        // 	duration: 4000
        // });
      })
      .catch(() => { console.log('Something went wrong'); });
  }

  getTemplatesDesign(
    collection: string,
    idBOT: string,
  ): Observable<Templates[]> {
    return this.afs
      .collection<Templates>(`${collection}/${idBOT}/templates`, (ref) => {
        let query = ref.orderBy('rang', 'asc');
        return query;
      })
      .snapshotChanges()
      .pipe(
        map((template) => {
          return template.map((a) => {
            const data = a.payload.doc.data() as Templates;
            const id = a.payload.doc.id;
            const path = a.payload.doc.ref.path;
            return {
              ...data,
              id,
              path,
            };
          });
        }),
      );
  }

  getLabels(idBOT: string): Observable<Labels[]> {
    return this.afs
      .collection<Labels>(`bots/${idBOT}/labels`)
      .snapshotChanges()
      .pipe(
        map((actions) =>
          actions.map((a) => {
            const data = a.payload.doc.data() as Labels;
            const id = a.payload.doc.id;
            const path = a.payload.doc.ref.path;
            return {
              id,
              path,
              ...data,
            };
          }),
        ),
      );
  }

  getGaActionWeb(action: any, surface: any): Observable<any[]> {
    return this.afs
      .collection<any>(`${action.path}/${surface}`)
      .snapshotChanges()
      .pipe(
        map((actions) => {
          return actions
            .map((a) => {
              const data = a.payload.doc.data() as any;
              const id = a.payload.doc.id;
              const path = a.payload.doc.ref.path;
              return {
                id,
                path,
                ...data,
              };
            })
            .sort((a, b) => (a.index > b.index ? 1 : -1));
        }),
      );
  }

  getGaActionWebResponse(action: any, surface: any): Observable<any[]> {
    return this.afs
      .collection<any>(`${action.path}/${surface}`)
      .snapshotChanges()
      .pipe(
        map((actions) => {
          return actions
            .map((a) => {
              const data = a.payload.doc.data() as any;
              const id = a.payload.doc.id;
              const path = a.payload.doc.ref.path;
              return {
                id,
                path,
                ...data,
              };
            })
            .sort((a, b) => (a.index > b.index ? 1 : -1));
        }),
      );
  }

  getGaActionWebBuilt(action: any, surface: any): Observable<any[]> {
    return this.afs
      .collection<any>(`${action.path}/${surface}`)
      .snapshotChanges()
      .pipe(
        map((actions) => {
          return actions
            .map((a) => {
              const data = a.payload.doc.data() as any;
              const id = a.payload.doc.id;
              const path = a.payload.doc.ref.path;
              return {
                id,
                path,
                ...data,
              };
            })
            .sort((a, b) => (a.index > b.index ? 1 : -1));
        }),
      );
  }

  addResponseGAActionElement(
    idBOT: string,
    actID: string,
    surface: string,
    element: any,
  ): Promise<any> {
    let userID: string;
    try {
      userID = this.getRequiredUserId();
    } catch (error) {
      return Promise.reject(error);
    }
    const subRef = this.afs
      .collection(
        `${this.auth.isSuperAdmin() ? 'default-bots' : 'bots'}/${idBOT}/actions/`,
      )
      .doc(actID)
      .collection(surface).ref;
    const payload = { ...Object.assign({}, element), userID };
    const requestedIndex = Number(payload?.index ?? 1);

    const writePromise =
      requestedIndex > 1
        ? subRef.add({ ...payload, index: requestedIndex }).then(() => {})
        : subRef.get().then((snap) => {
            const docs = snap.docs;
            const firstDoc = docs.find((d) => Number(d.data()?.index ?? 0) === 1);
            const maxIndex = docs.reduce(
              (m, d) => Math.max(m, Number(d.data()?.index ?? 0)),
              0,
            );

            const isPlaceholder =
              !!firstDoc &&
              firstDoc.data()?.type === 'message' &&
              firstDoc.data()?.type3 === 'response' &&
              firstDoc.data()?.recoMessage === false;

            if (firstDoc && isPlaceholder) {
              return firstDoc.ref.set({ ...payload, index: 1 });
            }

            const finalIndex = maxIndex + 1;
            return subRef.add({ ...payload, index: finalIndex }).then(() => {});
          });

    return writePromise
      .then(() => {
      })
      .catch(() => { console.log('Something went wrong'); });
  }

  addResponseGAActionElementDesign(
    collection: string,
    idBOT: string,
    actID: string,
    surface: string,
    element: any,
  ): Promise<any> {
    let userID: string;
    try {
      userID = this.getRequiredUserId();
    } catch (error) {
      return Promise.reject(error);
    }
    const subRef = this.afs
      .collection(`${collection}/${idBOT}/actions/`)
      .doc(actID)
      .collection(surface).ref;
    const payload = { ...Object.assign({}, element), userID };
    const requestedIndex = Number(payload?.index ?? 1);

    const writePromise =
      requestedIndex > 1
        ? subRef.add({ ...payload, index: requestedIndex }).then(() => {})
        : subRef.get().then((snap) => {
            const docs = snap.docs;
            const firstDoc = docs.find((d) => Number(d.data()?.index ?? 0) === 1);
            const maxIndex = docs.reduce(
              (m, d) => Math.max(m, Number(d.data()?.index ?? 0)),
              0,
            );

            const isPlaceholder =
              !!firstDoc &&
              firstDoc.data()?.type === 'message' &&
              firstDoc.data()?.type3 === 'response' &&
              firstDoc.data()?.recoMessage === false;

            if (firstDoc && isPlaceholder) {
              return firstDoc.ref.set({ ...payload, index: 1 });
            }

            const finalIndex = maxIndex + 1;
            return subRef.add({ ...payload, index: finalIndex }).then(() => {});
          });

    return writePromise
      .then(() => {
      })
      .catch(() => { console.log('Something went wrong'); });
  }

  addfulfilment(
    collection: string,
    idBot: string,
    actID: string,
    actionName: string,
    builtin: boolean,
    event: boolean,
    intention: boolean,
    show: boolean,
    builtTemplate: boolean,
    elements: any, // Adjust the type as needed for your specific data structure
    promptList: any, // Adjust the type as needed for your specific data structure
    parent: string | null, // Assuming parent is a string or null
  ): Promise<any> {
    let userID: string;
    try {
      userID = this.getRequiredUserId();
    } catch (error) {
      return Promise.reject(error);
    }
    const actionRef = this.afs
      .collection(`${collection}/${idBot}/actions/`)
      .doc(actID);


    return actionRef
      .set({
        parent: parent,
        name: actionName,
        show: show,
        built: builtin,
        builtTemplate: builtTemplate,
        event: event,
        intention: intention,
        promptList: promptList,
        userID,
        FlagSaved: {
          AUDIO_OUTPUT: false,
          SCREEN_OUTPUT: false,
          WEB_BROWSER: false,
        },
        isReco: {
          AUDIO_OUTPUT: false,
          SCREEN_OUTPUT: false,
          WEB_BROWSER: false,
        },
        help: {
          AUDIO_OUTPUT: '',
          SCREEN_OUTPUT: '',
          WEB_BROWSER: '',
          type_AUDIO_OUTPUT: 'preset',
        },
        suggestions: {
          AUDIO_OUTPUT: '',
          SCREEN_OUTPUT: '',
          WEB_BROWSER: '',
        },
        timeStamp: Date.now(),
      })
      .then(() => {
        //TODO: add index into element, the order at which this element response will be shown @ bit's responses
        const elementPayload = { ...(elements || {}), userID };
        return Promise.all([
          actionRef.collection('WEB_BROWSER').add(elementPayload),
          actionRef.collection('SCREEN_OUTPUT').add(elementPayload),
          actionRef.collection('AUDIO_OUTPUT').add(elementPayload),
        ]);
      })
      .catch(() => { console.log('Something went wrong'); })
      .then(() => {
        if (parent) {
          this.afs.doc(`${parent}`).update({
            // children: firebase.firestore.FieldValue.arrayUnion(actID)
          });
        }
      })
      .then(() => {
        return actionRef.snapshotChanges().pipe(
          map((a) => {
            const data = a.payload.data() as Actions;
            const id = a.payload.id;
            const path = a.payload.ref.path;
            return {
              id,
              ...data,
            };
          }),
        );
      });
  }

  addIntentGa(
    collection: string,
    idBot: string,
    intentID: string,
    IntentName: string,
    userSays: any,
    type: any,
    action: any,
    show: any,
  ): Promise<any> {
    const IntentRef = this.afs
      .collection(`${collection}/${idBot}/intents/`)
      .doc(intentID);
    return IntentRef.set({
      name: IntentName,
      type: type,
      action: action,
      dialogFlowAction: action,
      userSays: userSays,
      auto: true,
      show: show,
      timeStamp: Date.now(),
    }).then(() => {
      return IntentRef.snapshotChanges().pipe(
        map((a) => {
          const data = a.payload.data() as Intents;
          const id = a.payload.id;
          const path = a.payload.ref.path;
          return {
            ...data,
          };
        }),
      );
    });
  }

  getActionEgalements(action: any, surface: string): Observable<any[]> {
    return this.afs
      .collection<any>(`${action.path}/${surface}`)
      .snapshotChanges()
      .pipe(
        map((actions) => {
          return actions
            .map((a) => {
              const data = a.payload.doc.data() as any;

              const id = a.payload.doc.id;

              const path = a.payload.doc.ref.path;
              return {
                id,
                path,
                ...data,
              };
            })
            .sort((a, b) => (a.index > b.index ? 1 : -1));
        }),
      );
  }

  updateIntention(
    collection: string,
    idBOTintent: string,
    intentId: string,
    IntentName: string,
  ): Promise<any> {
    return this.afs
      .collection(`${collection}/${idBOTintent}/intents/`)
      .doc(intentId)
      .ref.update({
        name: IntentName,
      })
      .then(() => {
      })
      .catch(() => { console.log('Something went wrong'); });
  }

  /* Widget*/
  getWidget(collection: string, idBOT: string): Observable<Widgets[]> {
    if (!collection || !idBOT) {
      this.loggingService.error('Invalid parameters in getWidget:', {
        collection,
        idBOT,
      });
      return new Observable((observer) => {
        observer.error(new Error('Invalid collection or bot ID provided'));
        observer.complete();
      });
    }

    const path = `${collection}/${idBOT}/widgets`;
    return this.afs
      .collection<Widgets>(path)
      .snapshotChanges()
      .pipe(
        map((intents) => {
          return intents
            .map((a) => {
              const data = a.payload.doc.data() as Widgets;
              const id = a.payload.doc.id;
              const path = a.payload.doc.ref.path;
              return {
                id,
                ...data,
              };
            })
            .sort((a, b) => (a.timeStamp < b.timeStamp ? 1 : -1));
        }),
        catchError((error) => {
          this.loggingService.error('[Firebase ERROR in getDocument()]', error);
          this.loggingService.error('Path attempted:', path);
          return of([]);
        }),
      );
  }

  addWidget(
    collection: string,
    idBot: string,
    widgetId: string,
    widgetName: string,
  ): Promise<any> {
    let userID: string;
    try {
      userID = this.getRequiredUserId();
    } catch (error) {
      return Promise.reject(error);
    }
    const widgetRef = this.afs
      .collection(`${collection}/${idBot}/widgets/`)
      .doc(widgetId);
    return widgetRef
      .set({
        name: widgetName,
        timeStamp: Date.now(),
        userID,
      })
      .then(() => {
        return widgetRef.snapshotChanges().pipe(
          map((a) => {
            const data = a.payload.data() as Widgets;
            const id = a.payload.id;
            const path = a.payload.ref.path;
            return {
              id,
              ...data,
            };
          }),
        );
      });
  }

  deleteWidget(docpath: string): Promise<any> {
    return this.afs
      .doc(docpath)
      .delete()
      .then(() => {})
      .catch(() => { console.log('Something went wrong'); });
  }

  delete(docpath: string): Promise<any> {
    return this.afs
      .doc(docpath)
      .delete()
      .then(() => {})
      .catch(() => { console.log('Something went wrong'); });
  }
  updateWidget(docpath: string, element: string): Promise<any> {
    return this.afs
      .doc(docpath)
      .update(element)
      .then(() => {
      })
      .catch(() => { console.log('Something went wrong'); });
  }

  updateWidgetName(
    collection: string,
    idBOT: string,
    widgetId: string,
    widgetName: string,
  ): Promise<any> {
    return this.afs
      .collection(`${collection}/${idBOT}/widgets/`)
      .doc(widgetId)
      .ref.update({
        name: widgetName,
      })
      .then(() => {
      })
      .catch(() => { console.log('Something went wrong'); });
  }

  /* ------------------------themes------------------------ */
  addTheme(
    idTheme: string,
    themeName: string,
    botType: string,
    lang: string,
    platform: string,
    userID: string,
  ): Promise<any> {
    return this.afs
      .collection(`themes`)
      .doc(idTheme)
      .set({
        botName: themeName,
        platform: platform,
        botType: botType,
        defaultBot: false,
        defaultShopbotStatus: false,
        initDialogFlow: true,
        language: lang,
        userID: userID,
        type: 'theme',
        timeStamp: Date.now(),
      })
      .then(() => {});
  }

  updatetheme(themeID: string, themeName: string): Promise<any> {
    return this.afs
      .collection(`themes/`)
      .doc(themeID)
      .ref.update({
        name: themeName,
      })
      .then(() => {
      })
      .catch(() => { console.log('Something went wrong'); });
  }

  getthemes(userID: string): Observable<any[]> {
    return this.getCollectionWhere(
      ['themes'],
      [
        {
          left: 'userID',
          operator: '==',
          right: userID,
        },
      ],
    );
  }

  getthemesById(themeId: string): Observable<any[]> {
    return this.getCollectionWhere(
      ['themes'],
      [
        {
          left: 'themeId',
          operator: '==',
          right: themeId,
        },
      ],
    );
  }

  getUsersbots(userID: string): Observable<any[]> {
    return this.getCollectionWhere(
      ['bots'],
      [
        {
          left: 'userID',
          operator: '==',
          right: userID,
        },
      ],
    );
  }

  getStoreBots(storeId: string, userID: string): Observable<any[]> {
    return this.getCollectionWhere(
      ['bots'],
      [
        {
          left: 'cmsShop',
          operator: '==',
          right: storeId,
        },
        {
          left: 'userID',
          operator: '==',
          right: userID,
        },
      ],
    );
  }
  getDefaulBots(idDefaultWorkspace: string, userID: string): Observable<any[]> {
    // Avoid querying when user is not authenticated; prevents permission errors
    if (!this.hasAuthenticatedUser()) {
      return of([]);
    }
    return this.afs
      .collection<any>('default-bots', (ref) =>
        ref.where('idDefaultWorkspace', '==', idDefaultWorkspace),
      )
      .snapshotChanges()
      .pipe(
        filter(
          (actions) =>
            !actions.some((a) => a.payload.doc.metadata.fromCache),
        ),
        map((actions) =>
          actions.map((a) => {
            const data = a.payload.doc.data();
            const id = a.payload.doc.id;
            const path = a.payload.doc.ref.path;
            return {
              path,
              id,
              ...data,
            };
          }),
        ),
        catchError((error) => {
          // Suppress errors when user is no longer authenticated (e.g., after logout)
          if (this.hasAuthenticatedUser()) {
            this.loggingService.error(
              '[Firebase ERROR in getDefaulBots()]',
              error,
            );
          }
          return of([]);
        }),
      );
  }

  getAllbots(): Observable<any[]> {
    return this.afs
      .collection<any>(`bots`)
      .snapshotChanges()
      .pipe(
        map((actions) =>
          actions
            .map((a) => {
              const data = a.payload.doc.data();

              const id = a.payload.doc.id;
              const path = a.payload.doc.ref.path;
              return {
                path,
                id,
                ...data,
              };
            })
            .sort((a, b) => (a.timeStamp < b.timeStamp ? 1 : -1)),
        ),
      );
  }

  getAllThemes(): Observable<any[]> {
    return this.afs
      .collection<any>(`themes`)
      .snapshotChanges()
      .pipe(
        map((actions) =>
          actions
            .map((a) => {
              const data = a.payload.doc.data();

              const id = a.payload.doc.id;
              const path = a.payload.doc.ref.path;
              return {
                path,
                id,
                ...data,
              };
            })
            .sort((a, b) => (a.timeStamp < b.timeStamp ? 1 : -1)),
        ),
      );
  }

  // get cataogs list
  getUserscatalogs(userID: string): Observable<any[]> {
    return this.getCollectionWhere(
      ['catalogs'],
      [
        {
          left: 'userID',
          operator: '==',
          right: userID,
        },
      ],
    );
  }

  getUsersOrders(userID: string): Observable<any[]> {
    return this.getCollectionWhere(
      ['orders'],
      [
        {
          left: 'userID',
          operator: '==',
          right: userID,
        },
      ],
    );
  }

  addResponseAlexaActionElementDesign(
    collection: string,
    idBOT: string,
    actID: string,
    surface: string,
    element: any,
  ): Promise<any> {
    return this.afs
      .collection(`${collection}/${idBOT}/actions/`)
      .doc(actID)
      .collection(surface)
      .add(Object.assign({}, element))
      .then(() => {})
      .catch(() => { console.log('Something went wrong'); });
  }

  getAllcatalogs(): Observable<any[]> {
    return this.afs
      .collection<any>(`catalogs`)

      .snapshotChanges()
      .pipe(
        map((actions) =>
          actions
            .map((a) => {
              const data = a.payload.doc.data() as Catalogs;

              const id = a.payload.doc.id;
              const path = a.payload.doc.ref.path;
              return {
                path,
                ...data,
              };
            })
            .sort((a, b) => (a.timeStamp < b.timeStamp ? 1 : -1)),
        ),
      );
  }

  // get products
  getAllproducts(idCatalog: string): Observable<any[]> {
    return this.afs
      .collection<any>(`catalogs/${idCatalog}/products`)
      .snapshotChanges()
      .pipe(
        map((actions) =>
          actions.map((a) => {
            const data = a.payload.doc.data() as Products;

            const id = a.payload.doc.id;
            const path = a.payload.doc.ref.path;
            return {
              id,
              ...data,
            };
          }),
        ),
      );
  }
  // get options
  getAlloptions(idCatalog: string): Observable<any[]> {
    return this.afs
      .collection<any>(`catalogs/${idCatalog}/options`)
      .snapshotChanges()
      .pipe(
        map((actions) =>
          actions.map((a) => {
            const data = a.payload.doc.data() as Options;

            const id = a.payload.doc.id;
            const path = a.payload.doc.ref.path;
            return {
              id,
              ...data,
            };
          }),
        ),
      );
  }

  // getAllvariants(idCatalog:string): Observable<any[]> {
  // 	return this.getCollectionWhereWithoutTime([(`catalogs/${idCatalog}/options`)], [, {
  // 		left: 'usedforvariants',
  // 		operator: '==',
  // 		right: true
  // 	}]);
  // }

  addCatalog(
    platform: string,
    storeId: string,
    catalogData: any,
  ): Promise<any> {
    return this.afs
      .collection(`${platform}/${storeId}/catalogs`)
      .add(catalogData);
  }

  getOptionsUsedforVariants(idCatalog: string): Observable<any[]> {
    return this.getCollectionWhereWithoutTime(
      [`catalogs/${idCatalog}/options`],
      [
        {
          left: 'usedforvariants',
          operator: '==',
          right: true,
        },
      ],
    );
  }

  getOptions(idCatalog: string): Observable<any[]> {
    return this.getCollectionWhereWithoutTime(
      [`catalogs/${idCatalog}/options`],
      [
        {
          left: 'usedforvariants',
          operator: '==',
          right: false,
        },
      ],
    );
  }

  getAlloptionsid(idCatalog: string): Observable<any[]> {
    return this.afs
      .collection<Options>(`catalogs/${idCatalog}/options`)
      .snapshotChanges()
      .pipe(
        map((intents) => {
          return intents.map((a) => {
            const data = a.payload.doc.data() as Options;
            const id = a.payload.doc.id;
            const path = a.payload.doc.ref.path;
            return {
              id,
              ...data,
            };
          });
        }),
      );
  }
  //New
  getCollectionWhereOrderByrang(
    pathArray: string[],
    conditions: Condition[],
  ): Observable<any[]> {
    const coPath = pathArray.join('/');
    return this.afs
      .collection<any>(coPath, (ref) => {
        const cnd0 = conditions.pop();
        if (!this.tools.isUndefined(cnd0)) {
          let query = ref
            .where(cnd0?.left, cnd0?.operator, cnd0?.right)
            .orderBy('rang', 'asc');
          conditions.map(({ left, operator, right }: Condition) => {
            query = query.where(left, operator, right);
          });
          return query;
        } else {
          return ref;
        }
      })
      .snapshotChanges()
      .pipe(
        map((actions) => {
          return actions.map((a) => {
            const data = a.payload.doc.data() as any;
            const id = a.payload.doc.id;
            const path = a.payload.doc.ref.path;
            return {
              id,
              path,
              ...data,
            };
          });
        }),
        catchError((error: any) => {
          // Handle Firestore index errors
          if (
            error?.code === 'failed-precondition' &&
            error?.message?.includes('index')
          ) {
            // Extract index creation URL from error if available
            const indexUrlMatch = error?.message?.match(
              /https:\/\/console\.firebase\.google\.com[^\s]+/,
            );
            // const indexUrl = indexUrlMatch ? indexUrlMatch[0] : 'https://console.firebase.google.com/project/conversell-dev-b1562/firestore/indexes';
            const indexUrl = indexUrlMatch
              ? indexUrlMatch[0]
              : 'https://console.firebase.google.com/project/conversell-dev-b1562/firestore/indexes';

            this.notificationService.showError('Firestore index required. Please create the composite index:');

            // Log the index creation URL prominently
            this.notificationService.showError('color: red; font-size: 14px; font-weight: bold;');

            // Return empty array instead of throwing - allows UI to continue working
            // The index will be created and query will work after refresh
            return of([]);
          }

          // Handle permission errors - might occur when index is missing or user lacks permissions
          if (
            error?.code === 'permission-denied' ||
            error?.message?.includes('Missing or insufficient permissions')
          ) {
            this.notificationService.showWarning('Permission denied for this query');

            // Return empty array to prevent UI breakage
            return of([]);
          }

          // For other errors, log and return empty array
          this.loggingService.error(
            'Error in getCollectionWhereOrderByrang:',
            error,
          );
          return of([]);
        }),
      );
  }

  getTemplatesWhereCondition(
    collection: string,
    idBOT: string,
    condition: string,
  ): Observable<Templates[]> {
    return this.getCollectionWhereOrderByrang(
      [`${collection}/${idBOT}/templates/`],
      [
        {
          left: 'type',
          operator: '==',
          right: condition,
        },
      ],
    );
  }

  getGaTemplateSurface(template: any, surface: string): Observable<any[]> {
    return this.afs
      .collection<any>(`${template?.path}/${surface}`)
      .snapshotChanges()
      .pipe(
        map((templates) => {
          return templates.map((a) => {
            const data = a.payload.doc.data() as any;
            const id = a.payload.doc.id;
            const path = a.payload.doc.ref.path;
            return {
              id,
              path,
              ...data,
            };
          });
        }),
      );
  }
  getGaTemplateSurfaces(path: string, surface: string): Observable<any[]> {
    return this.afs
      .collection<any>(`${path}/${surface}`)
      .snapshotChanges()
      .pipe(
        map((templates) => {
          return templates.map((a) => {
            const data = a.payload.doc.data() as any;
            const id = a.payload.doc.id;
            const path = a.payload.doc.ref.path;
            return {
              id,
              path,
              ...data,
            };
          });
        }),
      );
  }
  addTemplateMSElementDesign(
    collection: string,
    idBOT: string,
    tempId: string,
    element: any,
  ): Promise<any> {
    let userID: string;
    try {
      userID = this.getRequiredUserId();
    } catch (error) {
      return Promise.reject(error);
    }
    const elementWithUserId = { ...element, userID };
    return this.afs
      .collection(`${collection}/${idBOT}/templates/`)
      .doc(tempId)
      .collection('elements')
      .add(Object.assign({}, elementWithUserId))
      .then(() => {
      })
      .catch(() => { console.log('Something went wrong'); });
  }
  getTemplates(idBOT): Observable<Templates[]> {
    return this.afs
      .collection<Templates>(
        `${this.auth.isSuperAdmin() ? 'default-bots' : 'bots'}/${idBOT}/templates`,
        (ref) => {
          let query = ref.orderBy('rang', 'asc');

          return query;
        },
      )
      .snapshotChanges()
      .pipe(
        map((template) => {
          return template.map((a) => {
            const data = a.payload.doc.data() as Templates;
            const id = a.payload.doc.id;
            const path = a.payload.doc.ref.path;
            return {
              id,
              path,
              ...data,
            };
          });
        }),
      );
  }

  updateTemplateNew(path: string, template: any): Promise<any> {
    return this.afs
      .doc(path)
      .ref.update(template)
      .catch(() => { console.log('Something went wrong'); })
      .then(() => {
      })
      .catch(() => { console.log('Something went wrong'); });
  }

  addTemplateGAElementDesign(
    collection: string,
    idBOT: string,
    tempId: string,
    surface: string,
    element: any,
  ): Promise<any> {
    return this.afs
      .collection(`${collection}/${idBOT}/templates/`)
      .doc(tempId)
      .collection(surface)
      .add(Object.assign({}, element))
      .then(() => {
      })
      .catch(() => { console.log('Something went wrong'); });
  }

  //Confirmation
  getGAconfirmationGA(confirmationPath: string): Observable<any[]> {
    return this.afs
      .collection<any>(`${confirmationPath}`)
      .snapshotChanges()
      .pipe(
        map((actions) => {
          return actions
            .map((a) => {
              const data = a.payload.doc.data() as any;

              const id = a.payload.doc.id;

              const path = a.payload.doc.ref.path;
              return {
                id,
                path,
                ...data,
              };
            })
            .sort((a, b) => (a.index > b.index ? 1 : -1));
        }),
      );
  }

  updateOptions(docpath: string, element: string): Promise<any> {
    return this.afs
      .doc(docpath)
      .update(element)
      .then(() => {
      })
      .catch(() => { console.log('Something went wrong'); });
  }

  updateProducts(docpath: string, element: string): Promise<any> {
    return this.afs
      .doc(docpath)
      .update(element)
      .then(() => {
      })
      .catch(() => { console.log('Something went wrong'); });
  }

  getOption(idCatalog: string, idOpt: string): Observable<any[]> {
    return this.afs
      .collection<any>(`catalogs/${idCatalog}/options/${idOpt}`)
      .snapshotChanges()
      .pipe(
        map((actions) => {
          return actions
            .map((a) => {
              const data = a.payload.doc.data() as any;
              const id = a.payload.doc.id;
              const path = a.payload.doc.ref.path;
              return {
                id,
                path,
                ...data,
              };
            })
            .sort((a, b) => (a.index > b.index ? 1 : -1));
        }),
      );
  }

  addOptions(idCatalog: string, element: string): Promise<any> {
    return this.afs
      .collection(`catalogs/${idCatalog}/options/` + 'testtest')
      .add(element)
      .then(() => {})
      .catch(() => { console.log('Something went wrong'); });
  }

  getthemesWherePlatform(paltform: string): Observable<any[]> {
    return this.getCollectionWhere(
      [`themes`],
      [
        {
          left: 'platform',
          operator: '==',
          right: paltform,
        },
      ],
    );
  }

  getOrdersWhereUserID(userID: string): Observable<Orders[]> {
    return this.getCollectionWhere(
      [`orders`],
      [
        {
          left: 'userID',
          operator: '==',
          right: userID,
        },
      ],
    );
  }

  getWoocommerceOrdersWhereUserID(
    userID: string,
    cmsShop: string,
  ): Observable<Orders[]> {
    return this.getCollectionWhereWithoutTime(
      [`woocommerceOrders/${cmsShop}/orders`],
      [
        {
          left: 'userID',
          operator: '==',
          right: userID,
        },
      ],
    );
  }

  getWoocommerceDraftOrdersWhereUserID(
    userID: string,
    cmsShop: string,
  ): Observable<Orders[]> {
    return this.getCollectionWhereWithoutTime(
      [`woocommerceDraftOrders/${cmsShop}/orders`],
      [
        {
          left: 'userID',
          operator: '==',
          right: userID,
        },
      ],
    );
  }

  getDraftOrdersWhereUserID(userID: string): Observable<DraftOrders[]> {
    return this.getCollectionWhere(
      [`draftOrders`],
      [
        {
          left: 'userID',
          operator: '==',
          right: userID,
        },
      ],
    );
  }

  addCategory(
    idCatalog: string,
    actID: string,
    categoryName: string,
    elements: any,
  ): Promise<any> {
    const actionRef = this.afs
      .collection(`catalogs/${idCatalog}/categories/`)
      .doc(actID);
    return actionRef
      .set({
        name: categoryName,
        timeStamp: Date.now(),
      })
      .then(() => {
        return actionRef.snapshotChanges().pipe(
          map((a) => {
            const data = a.payload.data() as Categories;
            const id = a.payload.id;
            const path = a.payload.ref.path;
            return {
              id,
              ...data,
            };
          }),
        );
      });
  }

  getCategories(idCatalog: string): Observable<any[]> {
    return this.afs
      .collection<any>(`catalogs/${idCatalog}/categories`)
      .snapshotChanges()
      .pipe(
        map((actions) =>
          actions.map((a) => {
            const data = a.payload.doc.data() as Categories;

            const id = a.payload.doc.id;
            const path = a.payload.doc.ref.path;
            return {
              id,
              ...data,
            };
          }),
        ),
      );
  }

  getAccessibleCategories(idCatalog: string): Observable<Categories[]> {
    return this.getCollectionWhereWithoutTime(
      [`catalogs/${idCatalog}/categories`],
      [
        {
          left: 'show',
          operator: '==',
          right: true,
        },
      ],
    );
  }

  findIntentwithPAYLAOD(
    path: string,
    payload: string,
  ): Observable<Categories[]> {
    return this.getCollectionWhereWithoutTime(
      [`${path}`],
      [
        {
          left: 'allpayloads',
          operator: 'array-contains',
          right: payload,
        },
      ],
    );
  }

  getuserID(userID: string): Observable<any[]> {
    return this.afs
      .collection<any>(`users`)
      .snapshotChanges()
      .pipe(
        map((actions) => {
          return actions
            .map((a) => {
              const data = a.payload.doc.data() as any;

              const id = a.payload.doc.id;

              const path = a.payload.doc.ref.path;
              return {
                id,
                path,
                ...data,
              };
            })
            .find((x) => x.id == userID);
        }),
      );
  }

  getOrdersbyId(idOrder: string): Observable<any[]> {
    return this.afs
      .collection<any>(`orders`)
      .snapshotChanges()
      .pipe(
        map((actions) => {
          return actions
            .map((a) => {
              const data = a.payload.doc.data() as any;
              const id = a.payload.doc.id;
              const path = a.payload.doc.ref.path;
              return {
                id,
                path,
                ...data,
              };
            })
            .find((x) => x.id == idOrder);
        }),
      );
  }

  getcatalogbyshopID(shop_ID: string): Observable<any[]> {
    return this.afs
      .collection<any>(`catalogs`)
      .snapshotChanges()
      .pipe(
        map((actions) => {
          return actions
            .map((a) => {
              const data = a.payload.doc.data() as any;

              const id = a.payload.doc.id;

              const path = a.payload.doc.ref.path;
              return {
                id,
                path,
                ...data,
              };
            })
            .find((x) => x.id == shop_ID);
        }),
      );
  }

  getActionPromptList(
    collection: string,
    idBOT: string,
  ): Observable<Actions[]> {
    return this.getCollectionWhereWithoutTime(
      [`${collection}/${idBOT}/actions`],
      [
        {
          left: 'promptList',
          operator: '==',
          right: true,
        },
      ],
    );
  }

  updatesettingscatalog(docpath: string, element: any): Promise<any> {
    return this.afs
      .doc(docpath)
      .update(element)
      .then(() => {
      })
      .catch(() => { console.log('Something went wrong'); });
  }
//updateStoreFavizoneId(this.platform, this.store.id, res.favizoneId, formData.modelId)

  updateStoreFavizoneId(
    collection: string,
    storeId: string,
    favizoneId: string,
    modelId?: string,
  ): Promise<any> {
    const storeUpdate: any = { favizoneId };
    if (modelId !== undefined) storeUpdate.modelId = modelId;

    return this.afs
      .collection(`${collection}`)
      .doc(storeId)
      .ref.update(storeUpdate)
      .then(() => {
      })
      .catch(() => { console.log('Something went wrong'); });
  } 
  getVaraint(
    collection: string,
    idCatalog: string,
    idProduit: string,
    key: string,
    option: string,
  ): Observable<Catalogs[]> {
    return this.getCollectionWhereWithoutTime(
      [`${collection}/${idCatalog}/products/${idProduit}/varaints/`],
      [
        {
          left: 'facets.' + key,
          operator: '==',
          right: option,
        },
        {
          left: 'show',
          operator: '==',
          right: true,
        },
      ],
    );
  }

  getnoVaraint(
    collection: string,
    idCatalog: string,
    idProduit: string,
  ): Observable<Catalogs[]> {
    return this.getCollectionWhereWithoutTime(
      [`${collection}/${idCatalog}/products/${idProduit}/varaints/`],
      [
        {
          left: 'show',
          operator: '==',
          right: true,
        },
      ],
    );
  }

  getSmallTalkIntents(
    collection: string,
    idBOT: string,
  ): Observable<Intents[]> {
    return this.getCollectionWhere(
      [`${collection}/${idBOT}/intents`],
      [
        {
          left: 'show',
          operator: '==',
          right: true,
        },
        {
          left: 'smallTalk',
          operator: '==',
          right: true,
        },
      ],
    );
  }

  getuserList(): Observable<any[]> {
    return this.afs
      .collection<any>(`users`, (ref) => ref.where('role', '==', 'admin'))
      .snapshotChanges()
      .pipe(
        map((users) => {
          return users.map((a) => {
            const data = a.payload.doc.data() as any;
            const id = a.payload.doc.id;
            const path = a.payload.doc.ref.path;
            return {
              id,
              path,
              ...data,
            };
          });
        }),
      );
  }

  getUsersByParentId(parentId: string): Observable<any[]> {
    return this.afs
      .collection<any>(`users`, (ref) =>
        ref.where('userParentId', '==', parentId),
      )
      .snapshotChanges()
      .pipe(
        map((users) => {
          return users.map((a) => {
            const data = a.payload.doc.data() as any;
            const id = a.payload.doc.id;
            const path = a.payload.doc.ref.path;
            return {
              id,
              path,
              ...data,
            };
          });
        }),
      );
  }
  getComplaintsList(): Observable<any[]> {
    return this.afs
      .collection<any>(`complaints`)
      .snapshotChanges()
      .pipe(
        map((complaints) => {
          return complaints.map((c) => {
            const data = c.payload.doc.data() as any;
            const id = c.payload.doc.id;
            const path = c.payload.doc.ref.path;
            return {
              id,
              path,
              ...data,
            };
          });
        }),
      );
  }

  /**
   * Meta messaging_policy_enforcement journal (DEV-03). Restricted to SuperAdmin by the
   * `compliance/metaPolicyEnforcement/events` rule in firestore.rules — the path is nested
   * so it escapes the `/{platform}/{workspaceId}` read-for-any-user wildcard.
   */
  getPolicyEnforcementEvents(): Observable<any[]> {
    return this.afs
      .collection<any>('compliance/metaPolicyEnforcement/events', (ref) =>
        ref.orderBy('received_at', 'desc'),
      )
      .snapshotChanges()
      .pipe(
        map((events) => {
          return events.map((e) => {
            const data = e.payload.doc.data() as any;
            const id = e.payload.doc.id;
            const path = e.payload.doc.ref.path;
            return {
              id,
              path,
              ...data,
            };
          });
        }),
      );
  }

  /**
   * Messenger / Instagram bots used by the Policy Enforcement simulate dialog.
   * SuperAdmin can query the whole `bots` collection; `pageID` is the Meta entry id
   * (Facebook Page id, or Instagram professional account id).
   */
  getMetaChannelBots(botType: 'messenger' | 'instagram'): Observable<any[]> {
    return this.getCollectionWhereWithoutTime(
      ['bots'],
      [{ left: 'botType', operator: '==', right: botType }],
    );
  }

  getshopsPlatform(platform: string): Observable<any[]> {
    return this.afs
      .collection<any>(`${platform}`)
      .snapshotChanges()
      .pipe(
        map((shops) => {
          return shops.map((a) => {
            const data = a.payload.doc.data() as any;
            const id = a.payload.doc.id;
            const path = a.payload.doc.ref.path;
            return {
              id,
              path,
              ...data,
            };
          });
        }),
      );
  }

  getUserShops(path: string): Observable<any[]> {
    return this.afs
      .collection<any>(`${path}`)
      .snapshotChanges()
      .pipe(
        map((actions) =>
          actions.map((a) => {
            const data = a.payload.doc.data() as any;
            const id = a.payload.doc.id;
            const path = a.payload.doc.ref.path;
            return {
              id,
              path,
              ...data,
            };
          }),
        ),
      );
  }

  getDraftOrdersWithpath(path: string): Observable<Orders[]> {
    return this.afs
      .collection<any>(`${path}`)
      .snapshotChanges()
      .pipe(
        map((actions) =>
          actions.map((a) => {
            const data = a.payload.doc.data() as Orders;
            const id = a.payload.doc.id;
            const path = a.payload.doc.ref.path;
            return {
              id,
              ...data,
            };
          }),
        ),
      );
  }

  getUserOrders(path: string, customer: any): Observable<any[]> {
    return this.getCollectionWhereWithoutTime(
      [`${path}`],
      [
        {
          left: 'customer.id',
          operator: '==',
          right: customer.id,
        },
      ],
    );
  }
  getUser(id: string): Observable<any> {
    return this.afs.collection<any>(`users`).doc(id).valueChanges().pipe();
  }
  getAllpreBuiltAgent(): Observable<any[]> {
    return this.afs
      .collection<any>(`/general/prebuiltAgents/dialogflow`)
      .snapshotChanges()
      .pipe(
        map((actions) =>
          actions.map((a) => {
            const data = a.payload.doc.data();

            const id = a.payload.doc.id;
            const path = a.payload.doc.ref.path;
            return {
              id,
              path,
              ...data,
            };
          }),
        ),
      );
  }

  getPackProduct(productId: string): Observable<any[]> {
    return this.afs
      .collection<any>(`general/packs/liste_packs/`)
      .snapshotChanges()
      .pipe(
        map((actions) => {
          return actions
            .map((a) => {
              const data = a.payload.doc.data() as any;

              const id = a.payload.doc.id;

              const path = a.payload.doc.ref.path;
              return {
                id,
                path,
                ...data,
              };
            })
            .find((x) => x.id == productId);
        }),
      );
  }

  async addIntentPrebuilt(path: string, body: any) {
    const IntentRef = this.afs.doc(path);
    await IntentRef.set(body, { merge: true }).then(() => {
      return IntentRef.snapshotChanges().pipe(
        map((a) => {
          const data = a.payload.data() as Intents;
          const id = a.payload.id;
          const path = a.payload.ref.path;
          return {
            ...data,
          };
        }),
      );
    });
  }

  getResponseStoresofuser(platform: string, userID: string): Observable<any[]> {
    return this.getCollectionWhereWithoutTime(
      [platform],
      [
        {
          left: 'userID',
          operator: '==',
          right: userID,
        },
      ],
    );
  }

  /**
   * Get all stores for a user across all supported platforms in a single operation
   * @param userID - The user ID to get stores for
   * @returns Observable containing all stores as a flat array
   */
  private normalizeStore(store: any, platform: string): any {
    const id = store?.id;
    const isBadName = (v?: string) => {
      const s = (v || '').trim();
      return (
        !s ||
        s === id ||
        s === store?.shopUuid ||
        /^[0-9a-f-]{36}$/i.test(s) ||
        (/^[A-Za-z0-9]{15,28}$/.test(s) && !/[.\- ]/.test(s))
      );
    };
    const current = store?.shopName || store?.name;
    if (!isBadName(current)) {
      return { ...store, platform };
    }
    const url = (store?.shopUrl || '')
      .replace(/^https?:\/\//, '')
      .split('/')[0]
      .replace(/^www\./, '');
    const cap = (x: string) =>
      x ? x.charAt(0).toUpperCase() + x.slice(1).toLowerCase() : '';
    const shopName =
      [store?.workspaceName, url]
        .find((v) => v && !isBadName(v)) ||
      [cap(store?.shopActivity || store?.activity || ''), cap(store?.platform || platform)]
        .filter(Boolean)
        .join(' ') ||
      'Workspace';
    return { ...store, platform, shopName };
  }

  getAllStoresForUser(userID: string): Observable<any[]> {
    const platforms = [
      'shopify',
      'woocommerce',
      'bigcommerce',
      'prestashop',
      'salla',
      'other',
    ];

    // Create observables for each platform with error handling
    const storeObservables = platforms.map((platform) =>
      this.getCollectionWhereWithoutTime(
        [platform],
        [
          {
            left: 'userID',
            operator: '==',
            right: userID,
          },
        ],
      ).pipe(
        take(1), // Complete after first emission since snapshotChanges() never completes
        map((stores) => {
          // Add platform info to each store
          return (stores || []).map((store) => this.normalizeStore(store, platform));
        }),
        catchError((error) => {
          this.loggingService.error('[Firebase ERROR in getDocument()]', error);
          this.loggingService.error('Path attempted:', platform);
          return of([]);
        }),
      ),
    );

    return forkJoin(storeObservables).pipe(
      map((results) => {
        // Flatten all stores into a single array
        return results.flat();
      }),
      catchError((error) => {
        this.loggingService.error('[Firebase ERROR in getDocument()]', error);
        this.loggingService.error('Path attempted:', userID);
        return of([]);
      }),
    );
  }

  /**
   * Alternative method to get all stores using direct Firestore queries
   * This method bypasses the getCollectionWhereWithoutTime method
   */
  getAllStoresForUserDirect(userID: string): Observable<any[]> {
    const platforms = [
      'shopify',
      'woocommerce',
      'bigcommerce',
      'prestashop',
      'salla',
      'custom',
    ];

    // Create observables for each platform with better error handling
    const storeObservables = platforms.map((platform) => {
      return this.afs
        .collection<any>(platform, (ref) => ref.where('userID', '==', userID))
        .snapshotChanges()
        .pipe(
          map((actions) => {
            const stores = actions.map((a) => {
              const data = a.payload.doc.data() as any;
              const id = a.payload.doc.id;
              const path = a.payload.doc.ref.path;
              return {
                id,
                path,
                platform: platform,
                ...data,
              };
            });
            return stores;
          }),
          catchError((error) => {
            this.loggingService.error(
              '[Firebase ERROR in getDocument()]',
              error,
            );
            this.loggingService.error('Path attempted:', platform);
            return of([]);
          }),
          // Add timeout to prevent hanging
          take(1),
        );
    });

    return forkJoin(storeObservables).pipe(
      map((results) => {
        // Flatten all stores into a single array
        const flattened = results.flat();
        return flattened;
      }),
      catchError((error) => {
        this.loggingService.error('[Firebase ERROR in getDocument()]', error);
        this.loggingService.error('Path attempted:', userID);
        return of([]);
      }),
      // Add timeout to prevent infinite waiting
      take(1),
    );
  }

  /**
   * Alternative method that processes platforms sequentially instead of using forkJoin
   */
  getAllStoresForUserSequential(userID: string): Observable<any[]> {
    const platforms = [
      'shopify',
      'woocommerce',
      'bigcommerce',
      'prestashop',
      'salla',
      'other',
    ];

    const platformObservables = platforms.map((platform) => {
      // Query with both userID and delete conditions to avoid filtering in JavaScript
      // This ensures we get fresh results from Firestore, not cached ones
      // Query with both userID and delete=false conditions directly in Firestore
      // This ensures we get fresh results and don't rely on JavaScript filtering
      return this.getCollectionWhereWithoutTime(
        [platform],
        [
          {
            left: 'userID',
            operator: '==',
            right: userID,
          },
        ],
      ).pipe(
        map((stores) => {
          // Stores are already filtered by Firestore query (delete == false)
          // But also filter out any stores where delete is undefined/null (for safety)

          // Log all found stores for debugging
          if (stores && stores.length > 0) {
            stores.forEach((store: any) => {});
          }

          // Additional safety filter for stores where delete is undefined/null
          const filteredStores = stores.filter((store) => {
            const shouldInclude =
              store.delete !== true && store.delete !== undefined;
            if (!shouldInclude) {
              return false;
            }
            return true;
          });
          return (stores || [])
            .filter((store) => store.delete !== true)
            .map((store) => this.normalizeStore(store, platform));
        }),
        catchError((error) => {
          this.loggingService.error('[Firebase ERROR in getDocument()]', error);
          this.loggingService.error('Path attempted:', platform);
          return of([]);
        }),
        // Ensure each observable completes
        take(1),
      );
    });

    return forkJoin(platformObservables).pipe(
      timeout(12000),
      map((results) => {
        // Flatten all stores into a single array
        const allStores = results.flat();
        return allStores;
      }),
      catchError((error) => {
        this.loggingService.error('[Firebase ERROR in getDocument()]', error);
        this.loggingService.error('Path attempted:', userID);
        return of([]);
      }),
      // Ensure each observable completes
      take(1),
    );
  }

  /**
   * Same query/filter behavior as getAllStoresForUserSequential, but for one platform collection only.
   */
  getStoresForUserByPlatform(
    userID: string,
    platform: string,
  ): Observable<any[]> {
    const collectionId = (platform || '').trim().toLowerCase();
    if (!userID || !collectionId) {
      return of([]);
    }

    return this.getCollectionWhereWithoutTime(
      [collectionId],
      [
        {
          left: 'userID',
          operator: '==',
          right: userID,
        },
      ],
    ).pipe(
      map((stores) =>
        (stores || [])
          .filter((store) => store.delete !== true)
          .map((store) => this.normalizeStore(store, collectionId)),
      ),
      catchError((error) => {
        this.loggingService.error(
          '[Firebase ERROR in getStoresForUserByPlatform]',
          error,
        );
        this.loggingService.error('Path attempted:', collectionId, userID);
        return of([]);
      }),
      take(1),
    );
  }

  getCatalogsofstore(path: string): Observable<any[]> {
    return this.afs
      .collection<any>(path)
      .snapshotChanges()
      .pipe(
        map((actions) => {
          return actions.map((a) => {
            const data = a.payload.doc.data() as any;
            const id = a.payload.doc.id;
            const path = a.payload.doc.ref.path;
            return {
              id,
              path,
              ...data,
            };
          });
        }),
      );
  }

  getDataBetweenTwoDates(
    path: string,
    startDate: number,
    data: any,
  ): Observable<any> {
    return this.getCollectionWhereWithoutTime(
      [path],
      [
        {
          left: data,
          operator: '>=',
          right: Number(startDate),
        },
      ],
    );
  }

  getStartDateIsDate(
    path: string,
    startDate: any,
    start: any,
  ): Observable<any[]> {
    return this.getCollectionWhereWithoutTime(
      [path],
      [
        {
          left: startDate,
          operator: '==',
          right: start,
        },
      ],
    );
  }

  getCollectionWhenChange(path): Observable<any> {
    return this.afs
      .collection(path)
      .valueChanges()
      .pipe(
        catchError((error) => {
          this.loggingService.error('[Firebase ERROR in getDocument()]', error);
          this.loggingService.error('Path attempted:', path);
          return of([]);
        }),
      );
  }

  getdurationBetweenTwoDates(
    path: string,
    startDate: any,
    endDate: any,
    data: any,
  ): Observable<any> {

    return this.getCollectionWhereWithoutTime(
      [path],
      [
        {
          left: data,
          operator: '>=',
          right: startDate,
        },
        {
          left: data,
          operator: '<=',
          right: endDate,
        },
      ],
    );
  }

  nbTotalConversation(
    pathArray: string[],
    key: string,
    startDate: Date,
    endDate: Date,
  ): Observable<any[]> {
    const bb = `nbConvPerDay[${key}]`;
    const coPath = pathArray.join('/');
    return this.afs
      .collection<any>(coPath, (ref) => {
        let query = ref
          .where(`${bb}`, '>=', startDate)
          .where(`${bb}`, '<=', endDate);
        return query;
      })
      .snapshotChanges()
      .pipe(
        map((actions) => {
          return actions.map((a) => {
            const data = a.payload.doc.data() as any;
            const id = a.payload.doc.id;
            const path = a.payload.doc.ref.path;
            return {
              id,
              path,
              ...data,
            };
          });
        }),
      );
  }

  getOldUsers(path: string, startDate: any, data: string): Observable<any[]> {
    return this.getCollectionWhereWithoutTime(
      [path],
      [
        {
          left: data,
          operator: '>=',
          right: startDate,
        },
      ],
    );
  }

  checkIfDocumentExists(
    collectionName: string,
    fieldName: string,
    operator: WhereFilterOp,
    value: any,
  ): Observable<boolean> {
    return this.afs
      .collection(collectionName, (ref) =>
        ref.where(fieldName, operator, value),
      )
      .valueChanges()
      .pipe(
        take(1),
        map((docs) => docs.length > 0),
      );
  }
  async saveIntegrationDfMessengenShopify(fileData, idBot, bot) {
    //  location of the file
    const fileRef = this.storage.ref(
      `uploads//bots//${idBot}//ScriptWidget.js`,
    );
    // writting javascript file and uploading it directly to firebase storage
    fileRef
      .putString(fileData, 'raw', { contentType: 'application/javascript' })
      //   detect the change
      .snapshotChanges()
      .pipe(
        //    when the file is created and the url is generated, call the api for shopify integration
        finalize(async () => {
          var body = {
            shop: bot.cmsShop,
            scriptTagID: bot.scriptTagID,
            scriptTagUrl: this.urlUpload,
          };
          // calling the api to update integration Url in shopify and make the change on the web page page
          // await this.RAS.postApi(`${environment.shopifyProdBaseURL[bot.botType]}/script-tag/update`, body).subscribe(async res => {
          // 	console.log(res)
          // 	if (res.status === "success")
          // 	{
          // 		this.snackBar.open('url integration created succefully' + this.urlUpload, '', { duration: 4000 });
          // 	}
          // 	else
          // 	{
          // 		console.log(res)
          // 		console.log(this.urlUpload)
          // 		this.snackBar.open('This may take some few minutes, Please try again  later!', '', { duration: 4000 });
          // 	}
          // })
        }),

        // getting the file url location
      )
      .subscribe(() =>
        fileRef.getDownloadURL().subscribe((urlUpload) => {
          this.urlUpload = urlUpload;
        }),
      );
  }
  async saveIntegrationDfMessengerForCustom(fileData, idBot, bot) {
    //  location of the file
    const fileRef = this.storage.ref(
      `uploads//bots//${idBot}//dialogflowMessengerScriptForCustom.js`,
    );
    // writting javascript file and uploading it directly to firebase storage
    fileRef
      .putString(fileData, 'raw', { contentType: 'application/javascript' })
      //   detect the change
      .snapshotChanges()
      .pipe(
        //    when the file is created and the url is generated, call the api for shopify integration
        finalize(async () => {
          var body = {
            shop: bot.cmsShop,
            scriptTagID: bot.scriptTagID,
            scriptTagUrl: this.urlUpload,
          };
          // calling the api to update integration Url in shopify and make the change on the web page page
          // await this.RAS.postApi(`${environment.shopifyProdBaseURL[bot.botType]}/script-tag/update`, body).subscribe(async res => {
          // if (res.status === "success")
          // {
          // 	this.snackBar.open('url integration created succefully' + this.urlUpload, '', { duration: 4000 });
          // }
          // else
          // {
          // 	console.log(res)
          // 	console.log(this.urlUpload)
          // 	this.snackBar.open('This may take some few minutes, Please try again  later!', '', { duration: 4000 });
          // }
          // })
        }),

        // getting the file url location
      )
      .subscribe(() =>
        fileRef.getDownloadURL().subscribe((urlUpload) => {
          let element = {
            scriptUrl: urlUpload,
          };
          return (
            this.afs
              .doc(
                `${this.auth.isSuperAdmin() ? 'default-bots' : 'bots'}/${bot.id}`,
              )
              .update(element)

              // this.afs.doc(docpath).update(element)
              .then()
              .catch(() => { console.log('Something went wrong'); })
          );
        }),
      );
  }
  updateFollowUp(
    collection: string,
    idBot: string,
    actID: string,
    actionName: string,
  ): Promise<any> {
    return this.afs
      .collection(`${collection}/${idBot}/FollowUpMessages/`)
      .doc(actID)
      .ref.update({
        name: actionName,
      })
      .then(() => {
      })
      .catch(() => { console.log('Something went wrong'); });
  }
  getAllFollowUpMessages(collection, idBot): Observable<any[]> {
    return this.afs
      .collection<any>(`${collection}/${idBot}/FollowUpMessages`)
      .snapshotChanges()
      .pipe(
        map((actions) => {
          return actions.map((a) => {
            const data = a.payload.doc.data() as any;
            const id = a.payload.doc.id;
            const path = a.payload.doc.ref.path;
            return {
              id,
              path,
              ...data,
            };
          });
        }),
      );
  }

  getAllMarketingMessages(collection, idBot): Observable<any[]> {
    return this.afs
      .collection<any>(`${collection}/${idBot}/MarketingMessages`)
      .snapshotChanges()
      .pipe(
        map((actions) => {
          return actions.map((a) => {
            const data = a.payload.doc.data() as any;
            const id = a.payload.doc.id;
            const path = a.payload.doc.ref.path;
            return {
              id,
              path,
              ...data,
            };
          });
        }),
      );
  }

  updateIntent(docpath, element): Promise<any> {
    return this.afs
      .doc(docpath)
      .update(element)
      .then(() => {
        // console.log('success');
        // this.snackBar.open('success', '', {
        // 	duration: 4000
        // });
      })
      .catch(() => { console.log('Something went wrong'); });
  }
  getAlldocuments(path): Observable<any[]> {
    return this.afs
      .collection<any>(path)
      .snapshotChanges()
      .pipe(
        map((documents) =>
          documents
            .map((a) => {
              const data = a.payload.doc.data();

              const id = a.payload.doc.id;
              const path = a.payload.doc.ref.path;
              return {
                id,
                path,
                ...data,
              };
            })
            .sort((a, b) => (a.timeStamp < b.timeStamp ? 1 : -1)),
        ),
      );
  }
  getCollection(path: string): Observable<any[]> {
    return this.afs
      .collection(path)
      .snapshotChanges()
      .pipe(
        map((actions) =>
          actions.map((a) => {
            const data: any = a.payload.doc.data();
            const id: string = a.payload.doc.id;
            return { id, ...data };
          }),
        ),
      );
  }

  addNewDoc(path, docName, body) {
    const docRef = this.afs.collection(path).doc(docName);
    return docRef.set(body, { merge: true }).then(() => {
      return docRef.snapshotChanges().pipe(
        map((a) => {
          const data = a.payload.data() as Intents;
          const id = a.payload.id;
          const path = a.payload.ref.path;
          return {
            ...data,
          };
        }),
      );
    });
  }

  updateDoc(path: string, docName: string, body: any) {
    const docRef = this.afs.collection(path).doc(docName);
    return docRef
      .update(body)
      .catch(() => { console.log('Something went wrong'); return docRef.set(body, { merge: true }); })
      .then(() => {
        return docRef.snapshotChanges().pipe(
          map((a) => {
            const data = a.payload.data() as any;
            return {
              ...data,
            };
          }),
        );
      });
  }

  /**
   * Get all documents from the default-workspaces collection sorted by createdAt
   * @returns Observable containing all default workspaces sorted by creation date (newest first)
   */
  getDefaultWorkspaces(): Observable<DefaultWorkspace[]> {
    return this.afs
      .collection<DefaultWorkspace>('default-workspaces')
      .snapshotChanges()
      .pipe(
        map((actions) => {
          const workspaces = actions.map((a) => {
            const data = a.payload.doc.data() as DefaultWorkspace;
            const id = a.payload.doc.id;
            const path = a.payload.doc.ref.path;
            return {
              ...data,
              id,
              path,
            } as DefaultWorkspace;
          });
          // orderBy('name') drops documents that only have shopName.
          const label = (workspace: DefaultWorkspace & { shopName?: string }) =>
            (workspace.name || workspace.shopName || '').toLowerCase();
          return workspaces.sort((a, b) => label(a).localeCompare(label(b)));
        }),
      );
  }

  // Get a specific flow by ID
  getFlowById(botId: string, flowId: string): Observable<any> {
    return from(
      this.afs
        .collection(`${this.auth.isSuperAdmin() ? 'default-bots' : 'bots'}`)
        .doc(botId)
        .collection('flows')
        .doc(flowId)
        .get(),
    ).pipe(
      timeout(5000), // 5 second timeout
      map((doc: any) => {
        if (!doc.exists) {
          return null;
        }
        const data = doc.data() as any;
        const id = doc.id;
        return { id, ...data };
      }),
      catchError((error) => {
        this.loggingService.error('[Firebase ERROR in getDocument()]', error);
        this.loggingService.error('Path attempted:', flowId);
        return of(null);
      }),
      take(1),
    );
  }
  removeResponseFromFlow(
    botId: string,
    flowId: string,
    responseId: string,
  ): Promise<void> {
    return this.afs
      .collection(`${this.auth.isSuperAdmin() ? 'default-bots' : 'bots'}`)
      .doc(botId)
      .collection('flows')
      .doc(flowId)
      .get()
      .pipe(
        take(1),
        switchMap((flowDoc) => {
          if (flowDoc && flowDoc.exists) {
            const flowData = flowDoc.data();
            const scenarios = flowData?.['scenarios'] || [];

            const updatedScenarios = scenarios.filter(
              (scenario) =>
                scenario.response !== responseId && scenario.id !== responseId,
            );

            return from(
              this.afs
                .collection(
                  `${this.auth.isSuperAdmin() ? 'default-bots' : 'bots'}`,
                )
                .doc(botId)
                .collection('flows')
                .doc(flowId)
                .update({ scenarios: updatedScenarios }),
            );
          }
          return of(undefined);
        }),
      )
      .toPromise();
  }

  deactivateFlow(botId: string, flowId: string): Promise<void> {
    return this.afs
      .collection(`${this.auth.isSuperAdmin() ? 'default-bots' : 'bots'}`)
      .doc(botId)
      .collection('flows')
      .doc(flowId)
      .update({ status: 'Draft' });
  }
}
